import { purchasePaymentBody } from "./purchase-payment.js";
import { performedByInput } from "./billing-links.js";
import { cashForecastCohortKeys, reportActivityCohorts } from "./report-drills.js";
import { reportRepairKinds } from "./category-period.js";
import { queueSortParam } from "./queue-sort.js";
/**
 * Request bodies, as zod schemas. The API validates with these; the web app
 * derives its form types from them, so a field the server will reject cannot
 * be typed as valid in the client.
 *
 * Money arrives as a **decimal string** and is validated by pattern, not by
 * `z.number()`. Coercing to a double here would be the same misrounding bug the
 * ledger constraints exist to prevent.
 */
import { z } from "zod";
import { addCostKinds, assetClassFilters, assetClasses, costStatuses, currencies, customFieldDataTypes, receiptReferenceCurrencies, downtimeReasons, faultSeverities, homeBaseMapBases, homeBaseMatchKinds, invoiceLineKinds, manuallyLoggedRequestSources, ownerships, priorities, queueStatusViews, readableRequestSources, statusReasonAvailabilities, stoppageReasons, timeKinds, userRoles, vendorStatuses, visibilities, workOrderIssueStatuses } from "./vocabulary.js";
import { bigintIdString, boundedDateString, INT4_MAX, isBigintId, isRealCalendarDay, phoneString, TEXT_MAX } from "./field-shapes.js";
// TRBUD-466: the vendor list's `missing` filter. That module imports nothing, so
// reading it here cannot close a cycle.
import { vendorCompletenessFields } from "./vendor-completeness.js";

/**
 * A money currency code: USD, the only one (Eden's ruling of 5 October 2026,
 * TRBUD-385; it was three from 8 September).
 *
 * ONE schema for every site that takes a money currency. Seven copies of a
 * pattern is how five of them stayed loose while `labour-rates` alone
 * normalised case, and how nobody noticed that all seven accepted `ZZZ`.
 *
 * `trim().toUpperCase()` before the enum, so `usd` resolves instead of 400-ing:
 * the same code in a different case is not "another currency".
 *
 * The message says what to do with a receipt in pesos or Canadian dollars,
 * because that is the caller most likely to be refused here: log the USD
 * charge, and keep the receipt's own currency as its reference
 * (`foreignReceipt` on the manual invoice body).
 */
export const currencyCode = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(z.enum(currencies, {
    message:
      "currency must be USD. TruckBuddy records money in US dollars only: log a receipt paid in " +
      "pesos or Canadian dollars at the USD amount charged, and keep its own currency as the receipt reference",
  }));

/** The same, defaulting to USD when the field is absent. */
export const currencyCodeDefaulted = currencyCode.default("USD");

/**
 * `z.object`, but it refuses a key it does not declare.
 *
 * UAT 0908 finding 13: the ~40 `.strict()` calls this repo added under
 * TRBUD-152 and TRBUD-176 were ALL on query schemas. `parseQuery` delegates
 * straight to `parseBody` (`apps/api/src/app.ts`), so strictness has never come
 * from the parser — only from the schema — and every request BODY silently
 * dropped unknown keys. `PATCH /api/settings/collectors` with a valid board plus
 * `"bogusKey": 1` answered 200.
 *
 * A helper at the OPENING of each declaration rather than a `.strict()` appended
 * to the end, for a mechanical reason worth knowing: many of these schemas carry
 * `.refine()` or `.superRefine()` chains, and `.strict()` has to sit on the
 * object rather than on the effect wrapping it. Anchoring on `z.object(` cannot
 * get that wrong, whereas finding "the end of the object literal" in a nested
 * shape can.
 *
 * `packages/contract/src/body-strictness.test.ts` derives the population from
 * the module exports and asserts the property behaviourally, so a new `*Body`
 * that forgets this is a failing test rather than a UAT finding.
 */
export function strictObject<T extends z.ZodRawShape>(shape: T) {
  return z.object(shape).strict();
}

/** Up to 2 decimal places, optional leading minus. Matches `numeric(10,2)`. */
export const decimalString = z
  .string()
  .trim()
  .regex(/^-?\d{1,8}(\.\d{1,2})?$/, "expected a decimal amount with at most 2 places");

/**
 * A WGS84 coordinate, and deliberately not `decimalString`.
 *
 * `maintenance_request.created_latitude` and `created_longitude` are
 * `numeric(9,6)` — three integer digits (±180 covers every longitude) and six
 * decimal places, which is about 0.1 m. `decimalString` allows two places,
 * because it exists for money and says so. Reusing it here silently caps a
 * position at roughly **1.1 km**, and because it is a `regex` rather than a
 * rounding step it does not cap at all — it REJECTS `41.878114` outright, so the
 * driver's coordinate never reaches the column at any precision.
 *
 * The range itself stays in the database, where the two CHECKs already live.
 */
export const coordinateString = z
  .string()
  .trim()
  .regex(/^-?\d{1,3}(\.\d{1,6})?$/, "expected a coordinate with at most 6 decimal places");

/**
 * A unit cost, and a quantity — neither of which is money at two decimal places.
 *
 * The SECOND time this branch hit the same defect. `decimalString` exists for
 * `numeric(10,2)` and says so; using it for a column with a different scale
 * REJECTS legitimate input rather than rounding it, because it is a regex.
 * Coordinates were the first case (`numeric(9,6)`); these are the next:
 *
 *   `purchase_order_line.unit_cost`         numeric(10,4)
 *   `purchase_order_line.quantity_ordered`  numeric(12,3)
 *
 * A vendor quoting $14.8000 a jug, or a fractional-cent price on a fastener, is
 * ordinary — and `part.unit_cost` is `numeric(10,4)` for exactly that reason.
 * The lesson is worth stating once here rather than rediscovering a third time:
 * **match the validator to the COLUMN, not to the nearest existing validator.**
 */
/**
 * One issue per refused value, worded for what is actually wrong with it.
 *
 * Added 2 October 2026. A regex can say only "does not match", so a minus sign
 * was reported as a precision fault: Eden's Line 67, a discount the receipt
 * reader wrote as `-1`, came back as *"expected a quantity with at most 3
 * decimal places"* when it had none to spare. The regexes, and so the values
 * each schema accepts, are unchanged; only the sentence a refusal carries is.
 * A minus sign is named first because it is the fault a person can see.
 */
function decimalFigure(options: {
  shape: RegExp;
  negative: string;
  shapeMessage: string;
  floor?: { test: (v: number) => boolean; message: string };
}) {
  return z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      if (value.startsWith("-")) {
        ctx.addIssue({ code: "custom", message: options.negative });
        return;
      }
      if (!options.shape.test(value)) {
        ctx.addIssue({ code: "custom", message: options.shapeMessage });
        return;
      }
      if (options.floor && !options.floor.test(Number.parseFloat(value))) {
        ctx.addIssue({ code: "custom", message: options.floor.message });
      }
    });
}

export const unitCostString = decimalFigure({
  shape: /^\d{1,6}(\.\d{1,4})?$/,
  negative: "a unit cost can't be negative",
  shapeMessage: "expected a unit cost with at most 4 decimal places",
});

export const quantityString = decimalFigure({
  shape: /^\d{1,9}(\.\d{1,3})?$/,
  negative: "a quantity can't be negative",
  shapeMessage: "expected a quantity with at most 3 decimal places",
  floor: { test: (v) => v > 0, message: "quantity must be greater than zero" },
});

/**
 * An invoice line's quantity and unit price — two places, never signed.
 *
 * Narrower than `quantityString` / `unitCostString` on purpose, and used ONLY by
 * `manualInvoiceLineInput`. Eden's ruling of 2 October 2026 (see
 * `INVOICE_LINE_SCALE` in `money.ts`) is about the invoice forms; the shared
 * validators also serve purchase-order lines, receipts against an order and part
 * prices, whose columns and callers still need three and four places. The
 * columns are unchanged: a two-place value fits `numeric(12,3)` and
 * `numeric(10,4)`.
 *
 * The messages are written to follow a field name — the form puts "Line 67
 * (LABOR DISCOUNT): quantity" in front of them — and say why a minus sign is
 * never needed, because the person reading them is looking at a discount.
 */
const INVOICE_NEGATIVE = "can't be negative; a discount's sign comes from its kind";
const INVOICE_SHAPE = "must be a number with at most 2 decimal places";

export const invoiceQuantityString = decimalFigure({
  shape: /^\d{1,9}(\.\d{1,2})?$/,
  negative: INVOICE_NEGATIVE,
  shapeMessage: INVOICE_SHAPE,
  floor: { test: (v) => v > 0, message: "must be more than 0" },
});

export const invoiceUnitCostString = decimalFigure({
  shape: /^\d{1,6}(\.\d{1,2})?$/,
  negative: INVOICE_NEGATIVE,
  shapeMessage: INVOICE_SHAPE,
});

/** Positive-only variant for the kinds whose CHECK requires `amount > 0`. */
export const positiveDecimalString = decimalString.refine(
  (v) => Number.parseFloat(v) > 0,
  "amount must be greater than zero",
);

/**
 * A magnitude that may be zero: no sign at all, so `-0` is refused too. For an
 * invoice line, where "no charge" is something the paper says.
 *
 * The same values as `decimalString` without its minus sign, as before
 * 2 October 2026; only the wording moved, to the invoice line's, so a refused
 * line total reads like a refused quantity. Its one caller is
 * `manualInvoiceLineInput.amount`.
 */
export const magnitudeDecimalString = decimalFigure({
  shape: /^\d{1,8}(\.\d{1,2})?$/,
  negative: INVOICE_NEGATIVE,
  shapeMessage: INVOICE_SHAPE,
});

export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "expected a zone-less date, YYYY-MM-DD");

/**
 * `isoDate` plus "and it is a day that exists" — for a date that reaches SQL.
 *
 * `isoDate` is a SHAPE check, so `2026-13-01` and `2026-02-30` both pass it.
 * That is harmless in a request body, where the value lands in a `date` column
 * and Postgres refuses it inside the write's own transaction. It is NOT harmless
 * in a query parameter that becomes a `WHERE ... < $1::date` comparison: the
 * cast raises `22008` in the middle of a read and the queue answers **500**,
 * naming nothing the caller could fix. A 400 that names the parameter is the
 * whole point of TRBUD-152, and a 500 is the one outcome it was meant to remove.
 *
 * Round-tripping through `Date.UTC` is what catches an overflowing day rather
 * than merely a wrong-looking one: `new Date("2026-02-30")` does not throw, it
 * rolls over to 2 March, so comparing the parts back is the check.
 *
 * That round trip is `isRealCalendarDay` in `field-shapes.ts` — the same question
 * asked as a plain predicate, because the BROWSER needs to ask it too (a date
 * field warns inline as the value is typed, and cannot run a Zod parse to find
 * out). This schema is the predicate plus a message; there is deliberately only
 * one implementation of the rule, since two copies of a date check are how
 * `2026-02-30` ends up refused in one place and accepted in another.
 */
export const calendarDate = isoDate.refine(isRealCalendarDay, "that date does not exist");

/**
 * An on/off filter arriving as a QUERY PARAMETER (TRBUD-172).
 *
 * ── Why `z.coerce.boolean()` is the wrong tool here ──────────────────
 * `z.coerce.boolean()` is `Boolean(value)`, and every query parameter arrives as
 * a **string**. `Boolean("false")` is `true`, so the schema turned the literal
 * word "false" into ON. Measured before this change:
 *
 *     z.object({ flaggedOnly: z.coerce.boolean().optional() })
 *       .parse({ flaggedOnly: "false" })      // => { flaggedOnly: true }
 *
 * Every non-empty string is truthy, so `"0"`, `"no"` and `"off"` were ON too.
 * The only string it got right was `""`.
 *
 * ── The defect that reached a screen ─────────────────────────────────
 * `buildUrl` in `apps/web/src/lib/api.ts` forwards a `boolean` (it skips only
 * `undefined` / `null` / `""`), so a caller sending the RAW value of a checkbox
 * puts `?flaggedOnly=false` on the wire. `Parts.tsx` and `Queue.tsx` both send
 * `x ? true : undefined`, which omits the key when off and so never hit it —
 * `PhonePreview.tsx` sends the raw boolean, so its parts list showed **only
 * flagged parts when the filter was off**. Fixed here rather than at that one
 * caller: the coercion is the thing that lies, and the next caller to send a
 * raw boolean would otherwise reintroduce it.
 *
 * ── Why `z.stringbool()` over a hand-rolled preprocess ───────────────
 * It is zod's own answer (present in the pinned 4.4.3), so the truthy/falsy
 * vocabulary is not ours to maintain: `true`/`1`/`yes`/`on`/`y`/`enabled` and
 * `false`/`0`/`no`/`off`/`n`/`disabled`, case-insensitively. `on` is what a bare
 * HTML checkbox posts, which is a useful accident rather than a designed one.
 *
 * Two refusals are deliberate, and both are LOUD rather than silent:
 *
 * - A **raw boolean** is `invalid_type`. No HTTP path can deliver one — Fastify's
 *   querystring values are strings — so it only arises from a direct `.parse()`
 *   in code, where a 400-shaped throw is the answer that gets noticed. This is
 *   what makes the defect above unreintroducible.
 * - An **empty value** (`?flaggedOnly=`) is `invalid_value` where it used to be
 *   `false`. That is the one input `Boolean()` handled correctly, and it is
 *   traded for a 400 knowingly: `buildUrl` never produces it, and refusing an
 *   ambiguous filter is the doctrine the neighbouring `queueQuery` cases already
 *   pin (F-37 — a filter that answers 200 and ignores you is the failure mode).
 *
 * An ABSENT key is still `undefined` and still means unfiltered, so this is not
 * a gate keyed on presence — nothing here can cold-start-softlock a screen.
 */
export const queryBoolean = z.stringbool();

/**
 * Every mutation carries one. B7/B8: `create_work_order` and the request /
 * comment / transition paths have no idempotency key in the database yet, so
 * for those the API keeps a short-lived in-process replay cache keyed on this
 * value. That is weaker than a unique index and is documented as such — but a
 * retried submit after a dropped connection must not create a second row.
 */
export const idempotencyKey = z.string().trim().min(8).max(200);

/**
 * The maintenance-request triage inbox.
 *
 * `.strict()` per TRBUD-152. Like `reportsQuery` this one carries defaults, so a
 * misspelled `?state=all` did not merely fail to widen the list — it fell back to
 * `status: "open"` and returned a 200 that looked like the full history and was
 * the open queue. A triager reading that concludes nothing was ever declined.
 */
/** The orders `GET /requests/inbox` can sort by. */
export const requestInboxSorts = ["needed_by", "newest", "priority"] as const;
export type RequestInboxSort = (typeof requestInboxSorts)[number];

export const requestsInboxQuery = z
  .object({
    status: z.enum(["open", "converted", "declined", "all"]).default("open"),
    source: z.enum(readableRequestSources).optional(),
    q: z.string().trim().max(200).optional(),
    /** Narrow to one priority. The status tabs' counts respect it too. */
    priority: z.enum(priorities).optional(),
    /**
     * Row order. Absent means `needed_by`: planned requests first, soonest
     * first, then the rest newest first (the order this read always had).
     * `newest` is plain created-at descending; `priority` is urgent, high,
     * normal, low, each band in the needed-by order.
     */
    sort: z.enum(requestInboxSorts).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .strict();
export type RequestsInboxQuery = z.infer<typeof requestsInboxQuery>;

/**
 * The driver screen's own query, and it exists for one field.
 *
 * `unit` searches the unit picker. Without it the picker was the first 25 active
 * assets and nothing else — and the requester role reaches no other route that
 * returns an asset id, so a driver whose unit sorted 26th could not file a
 * request at all. Matched against `unit_number` and `name` because a driver
 * knows their truck by either.
 *
 * `.strict()` per TRBUD-152, and the stakes are highest on this schema of the
 * ten. A driver who searched `?unitNumber=` instead of `?unit=` got the first 25
 * active assets back with a 200 — and since the requester role reaches no other
 * route returning an asset id, they would page through a list their own truck was
 * absent from with nothing saying the search had been ignored. The 400 is the
 * only version of that answer a driver can act on.
 */
export const driverContextQuery = z
  .object({
    unit: z.string().trim().min(1).max(100).optional(),
  })
  .strict();
export type DriverContextQuery = z.infer<typeof driverContextQuery>;

/**
 * At most this many fault types on one work order.
 *
 * Exported rather than restated in the browser. Two spellings of one rule is how
 * a control comes to compose a body the route refuses, and the categories cap was
 * exactly that until 16 September 2026: `EditCategoriesDialog` carried its own
 * `MAX_CATEGORIES = 20` beside a schema that already said 20, with nothing
 * comparing them. Both caps are now single definitions the dialogs import.
 */
export const MAX_WORK_ORDER_FAULTS = 20;

/**
 * At most this many categories on one work order.
 *
 * Exported for the same reason the fault cap is, and this one is the reason the
 * comment above exists: `EditCategoriesDialog` declared its own
 * `MAX_CATEGORIES = 20` beside a schema that already said 20, so the rule had
 * two spellings and nothing compared them. One export, one number.
 */
export const MAX_WORK_ORDER_CATEGORIES = 20;

/**
 * "Mark the asset out of service" — the publish-time option (Eden, 8 September
 * 2026), shared by the create and release bodies because it is one decision
 * reached through two doors.
 *
 * ── OPTIONAL, and the omission is the interesting case ───────────────
 * Omitted does NOT mean false. It means "nobody expressed a preference", and the
 * server then applies the default rule: **checked for repair work, unchecked for
 * preventive work**. That resolution lives on the server rather than in the
 * dialog on purpose — an API client, a future PM scheduler and the release dialog
 * must not be able to disagree about what a work order that says nothing means,
 * and the dialog's checkbox is rendered from the same rule so the user sees what
 * they are about to get.
 *
 * ── Where "preventive" comes from, and a correction ──────────────────
 * Eden's ruling names `work_order_type.is_preventive`. **That column does not
 * exist, and deliberately so.** `is_preventive` lives on `work_order_category`
 * (migration 0044), because of the 13 August 2026 ruling recorded in `erd.md` 9.8
 * and CLAUDE.md: *PM is a CATEGORY of maintenance work, not a work-order type,
 * and the Category axis governs.* MaintainX answers "is this PM?" twice and
 * inconsistently — `Preventive` is both a Type (145 rows) and a Category (204),
 * neither set containing the other — and having one answer is the improvement.
 *
 * So the rule reads against the work order's CATEGORIES: any linked category with
 * `is_preventive` makes the default unchecked, and everything else is repair work
 * and defaults checked. The intent of the ruling is preserved exactly; only the
 * column it names is different, and taking it literally would have meant either
 * inventing a second PM flag or failing to compile.
 *
 * ── Note this behaves differently on the two doors, honestly ─────────
 * `POST /work-orders` cannot set categories, so a work order created and released
 * in one call has none and always resolves to CHECKED. That is the right answer
 * for that door: with no PM signal available at that instant, the work is repair
 * work. Categories are set on the work-order screen, so the release path — which
 * is where the checkbox actually lives, and what Eden asked for — sees them.
 */
const markAssetOutOfService = z
  .boolean()
  .optional()
  .describe(
    "Open an asset_downtime interval for this work order's asset on publish. " +
      "Omit to take the default: checked for repair work, unchecked for preventive work.",
  );

/**
 * Work-order itemization: one line on the ticket. DECLARED ABOVE
 * `createWorkOrderBody`, which references it, because a zod schema is
 * evaluated at module load and a `const` used before its declaration is a
 * `ReferenceError` on the first import of `@truckbuddy/contract` — the
 * `MAX_WORK_ORDER_FAULTS` ordering trap CLAUDE.md records.
 *
 * Thirty lines on one ticket: generous for a walk-in list, small enough that
 * a runaway client cannot land a thousand rows through one request.
 */
export const MAX_WORK_ORDER_ISSUES = 30;

export const workOrderIssueInput = strictObject({
  description: z.string().trim().min(1).max(500),
  reasonTypeId: z.string().uuid().nullish(),
});
export type WorkOrderIssueInput = z.infer<typeof workOrderIssueInput>;

/**
 * A work order's title, trimmed, 1 to `TEXT_MAX.line` (500) characters. ONE
 * schema for create and for `PATCH /work-orders/:id/title` (TRBUD-311), so the
 * two bounds cannot drift apart.
 */
export const workOrderTitle = z.string().trim().min(1).max(TEXT_MAX.line);

/**
 * A title with every run of whitespace (line breaks included) collapsed to one
 * space and the ends trimmed. The "is this rename a no-op" comparison, used by
 * the route and by the dialog so they agree: a text input deletes line breaks
 * from its value, so a title cut from a multi-line Issue box must compare equal
 * to what the dialog shows.
 */
export function collapseTitleWhitespace(title: string): string {
  return title.replace(/\s+/g, " ").trim();
}

/**
 * The longest warranty note: what a vendor's warranty on a job covers (7 October
 * 2026). Declared ABOVE `createWorkOrderBody`, which reads it when the module
 * loads; below it, importing the package would throw.
 * `work_order_vendor_warranty_note_chk` holds the same 1000.
 */
export const WARRANTY_NOTE_MAX = 1000;

export const createWorkOrderBody = strictObject({
  assetId: z.string().uuid(),
  typeId: z.string().uuid(),
  title: workOrderTitle,
  description: z.string().trim().max(4000).optional(),
  priority: z.enum(priorities).default("normal"),
  dueDate: boundedDateString("scheduled").nullish(),
  /**
   * "Covered by warranty" at intake — TRBUD-237.
   *
   * `.default(false)` rather than required, unlike `setWorkOrderWarrantyBody`:
   * this body has many callers and an omitted flag honestly means "nobody said",
   * which is the column's own default. On the PATCH the field IS required,
   * because there a missing key would be an editing command that says nothing.
   */
  underWarranty: z.boolean().default(false),
  /**
   * What the warranty covers, when `underWarranty` is ticked (7 October 2026).
   * Warranty belongs to a vendor's work on the job, so the route records both
   * on `primaryVendorId` and refuses `underWarranty` without one.
   */
  warrantyNote: z.string().trim().max(WARRANTY_NOTE_MAX).nullish(),
  requestId: z.string().uuid().nullish(),
  parentWorkOrderId: z.string().uuid().nullish(),
  primaryVendorId: z.string().uuid().nullish(),
  rootCauseId: z.string().uuid().nullish(),
  /**
   * Why the work is being done — `work_order.reason_type_id`, acceptance
   * criterion A2's "structured fault type".
   *
   * `create_work_order` has taken `p_reason_type_id` since migration `0024` and
   * BOTH create paths passed a literal `NULL` in that argument position until
   * this field existed. Sitting beside `rootCauseId` is deliberate: the two are
   * the same shape and were wired at different times, which is exactly how the
   * gap went unnoticed — `root_cause_id` was threaded through and
   * `reason_type_id` was not.
   *
   * A SET since TRBUD-266. Eden, 15 September 2026, asked which shape and ruled
   * "drop reason_type_id, go with the set", so there is no primary fault and no
   * residual column — a truck can be in for two things at once and the record
   * has to be able to say so.
   *
   * It is NOT a parameter of `create_work_order` any more. Migration 0238
   * removed `p_reason_type_id`, and the route inserts the links immediately
   * afterwards in the same transaction, exactly as the draft flag and the
   * warranty box already do. Categories are handled the same way.
   *
   * Defaults to empty rather than being required, and that is the safety half of
   * the A2 work: the gate belongs on CLOSING, not on creating. A truck arrives
   * with a symptom, not a diagnosis, so demanding a fault up front would block
   * the create path to enforce a rule about the close path.
   *
   * Capped at 20, matching `setWorkOrderCategoriesBody`. No enum: `reason_type`
   * is user-managed configuration (requirement 11.24), so a hard-coded list here
   * would make a fault somebody added unusable until the front end shipped again.
   */
  reasonTypeIds: z.array(z.string().uuid()).max(MAX_WORK_ORDER_FAULTS).default([]),
  customFields: z.record(z.string(), z.unknown()).default({}),
  /**
   * "Save as draft" rather than "Save work order" — the 21 August ruling.
   *
   * Not a parameter of `create_work_order`, and deliberately so: migration 0068
   * records why. Adding a defaulted argument to that function would OVERLOAD it —
   * `CREATE OR REPLACE` with an extra argument creates a second function rather
   * than replacing the first — and dropping it to re-create it resets its
   * permissions to PUBLIC. So the API calls `create_work_order` and then
   * `mark_work_order_draft` inside one transaction, and this flag is what selects
   * the second call. There is no window where the row is briefly public: an
   * uncommitted row is invisible to every other transaction.
   */
  /**
   * The list on the ticket — work-order itemization. Meaningful on the `visit`
   * container type, where the route inserts one `work_order_issue` per entry in
   * the same transaction as `create_work_order`; accepted on any type so a
   * standalone work order can carry a checklist too. Empty by default, so every
   * existing caller is unchanged.
   */
  issues: z.array(workOrderIssueInput).max(MAX_WORK_ORDER_ISSUES).default([]),
  isDraft: z.boolean().default(false),
  /**
   * "Roadside breakdown" at intake — TRBUD-409. Sets `work_order.is_roadside`
   * by a follow-up UPDATE in the create's own transaction, the way the draft
   * flag and the warranty box are applied.
   *
   * OPTIONAL rather than `.default(false)`: `create_work_order` copies a
   * parent's flag onto a package, and an omitted key leaves that copy alone.
   * The dialog sends the box whenever it is ticked or a person touched it.
   */
  isRoadside: z.boolean().optional(),
  markAssetOutOfService,
  idempotencyKey,
});
export type CreateWorkOrderBody = z.infer<typeof createWorkOrderBody>;

/**
 * Releasing a draft. The key, plus the publish-time out-of-service option added
 * by migration 0216 — this body carried nothing but the key before that.
 *
 * The actor is the session, never a parameter — `release_work_order_draft` checks
 * that the caller IS the author and raises 42501 otherwise, so a body field naming
 * a user would be a claim the function ignores.
 *
 * There is no matching "un-release". A work order others have already seen cannot be
 * pulled back into privacy: their view of it would silently lose a row, and any
 * reference they made to it would dangle. The function refuses it.
 */
export const releaseDraftBody = strictObject({
  /**
   * The publish-time out-of-service option. See `markAssetOutOfService` above —
   * omitted means "take the default", not false.
   *
   * This is the door Eden actually described ("when publishing a work order it
   * comes with the option to mark the asset as oos"), so this is where the
   * release dialog's checkbox sends its value.
   */
  markAssetOutOfService,
  idempotencyKey,
});
export type ReleaseDraftBody = z.infer<typeof releaseDraftBody>;

export const transitionBody = strictObject({
  /**
   * The stage the client rendered. `transition_work_order` compare-and-swaps on
   * it and raises `40001` if the stage moved underneath — mapped to HTTP 409.
   * Never omit it and never send the stage read back moments earlier.
   */
  expectedFromStageId: z.string().uuid(),
  toStageId: z.string().uuid(),
  reason: z.string().trim().max(1000).optional(),
  idempotencyKey,
});
export type TransitionBody = z.infer<typeof transitionBody>;

/**
 * Cancelling a work order (TRBUD-95; widened to every open stage by PR #105).
 *
 * `reason` is required and this is the whole body: there is no target stage,
 * because the target is not the caller's to choose. The route resolves the
 * pipeline's own `cancelled` stage, which is what stops "cancel" from becoming a
 * generic "move it anywhere" with a friendlier name.
 *
 * ── Posted costs do NOT block a cancel ──────────────────────────────
 * Cancelling is a stage move and touches no ledger. `cost_entry` is append-only,
 * so money already posted stays on the work order and in the asset's cost
 * history exactly as it was, and a dbtest asserts it survives against
 * `cost_entry` and `v_asset_cost_history`. Until 8 September 2026 the route
 * refused any work order carrying a `cost_entry` — a rule from when cancel meant
 * "raised in error" — and PR #105 removed it on Eden's ruling that Delete is
 * cancel-and-hide from every stage that is not closed. (TRBUD-208 corrected this
 * comment, which had kept describing the removed refusal as live.)
 *
 * ── It is a transition, so it is in the history ─────────────────────
 * Nothing is deleted, and `asset_id` deliberately stays SELECT-only: repointing a
 * work order at the right asset fixes one failure mode of four and silently
 * rewrites history, because the costs, time and downtime already posted do not
 * move with it.
 */
/**
 * The stage code a pipeline's cancellation stage answers to, on the wire.
 *
 * Paired with `CANCELLED_STAGE_CODE` in
 * `packages/db/src/schema/core/work_order_pipelines.ts`, and the two MUST match.
 * They are two constants rather than one because `packages/db` holds the Postgres
 * URL and is Node-only — the browser bundle cannot import it, and adding a
 * dependency the other way would put the seeder's configuration types into every
 * client. Change one, change the other.
 *
 * A code rather than a `work_order_stage` column, which is the weaker option and
 * is worth naming: the table has `is_entry`, `is_completed` and `is_closed` but
 * nothing that says "this stage means cancelled". A dedicated flag is the proper
 * fix and is a schema change.
 */
export const CANCELLED_STAGE_CODE = "cancelled";

export const cancelWorkOrderBody = strictObject({
  reason: z.string().trim().min(3).max(1000),
  idempotencyKey,
});
export type CancelWorkOrderBody = z.infer<typeof cancelWorkOrderBody>;

/**
 * Removing a wrongly uploaded file from a work order (TRBUD-94).
 *
 * `reason` is REQUIRED and it is the only field that is. Detaching is the one
 * mutation in this file whose purpose is to take something away from everyone who
 * can see the work order, and `detach_work_order_document` records no reason of
 * its own — the audit trail gets it from here or not at all. "Wrong person's
 * paperwork" and "uploaded to the wrong job" are different facts a year later.
 *
 * There is no `undetach`. `work_order_document.detached_at` is a soft delete, so
 * the row survives for the audit trail, but every arm of `GET
 * /documents/:id/content` carries `detached_at IS NULL` — re-attaching would mean
 * a fresh attachment, deliberately, so the timeline says a person put it back.
 */
export const detachDocumentBody = strictObject({
  reason: z.string().trim().min(3).max(1000),
  idempotencyKey,
});
export type DetachDocumentBody = z.infer<typeof detachDocumentBody>;

/**
 * Attest that an attachment is the right receipt or invoice for this work order.
 *
 * NO REASON FIELD, deliberately, and the asymmetry with `detachDocumentBody` is
 * the point. Detaching removes evidence and has to explain itself. Verifying
 * ADDS an attestation, and the meaningful record is who attested and when —
 * `verified_by_user_id` and `verified_at`, which the routine stamps itself. A
 * free-text box here would collect "ok" and imply the attestation was the
 * sentence rather than the signature.
 */
export const verifyDocumentBody = strictObject({
  idempotencyKey,
});
export type VerifyDocumentBody = z.infer<typeof verifyDocumentBody>;

export const editDueDateBody = strictObject({
  dueDate: boundedDateString("scheduled").nullable(),
  idempotencyKey,
});
export type EditDueDateBody = z.infer<typeof editDueDateBody>;

/**
 * A work order's priority — `PATCH /work-orders/:id/priority`.
 *
 * Its OWN audited command, matching `/due-date` above rather than joining a
 * generic field patch, and for the reason `/vendor` states: a generic route
 * would need a column allow-list, which is a second place for the grant surface
 * to be restated and to drift.
 *
 * The manager raises and lowers this from the Repair overview board, where the
 * priority column a card sits in IS its priority — so the write and the read are
 * the same fact and a card that moved column while the row did not would be
 * indistinguishable from a scrambled board.
 *
 * A plain required enum over the shared `priorities` vocabulary, not
 * `.optional()`: the control names one of four columns and an absent key would
 * have to mean "leave it alone", which the control cannot express. The enum is
 * `priorities` itself rather than a copy, so the board's columns, the queue's
 * chips and `work_order_priority_chk` cannot disagree about what a priority is.
 */
export const setWorkOrderPriorityBody = strictObject({
  priority: z.enum(priorities),
  idempotencyKey,
});
export type SetWorkOrderPriorityBody = z.infer<typeof setWorkOrderPriorityBody>;

/**
 * Rename a work order — `PATCH /work-orders/:id/title` (TRBUD-311). Response is
 * `{ title: string }`.
 */
export const setWorkOrderTitleBody = strictObject({
  title: workOrderTitle,
  idempotencyKey,
});
export type SetWorkOrderTitleBody = z.infer<typeof setWorkOrderTitleBody>;

/**
 * "Needs attention" — `POST /work-orders/:id/attention`, raised by a manager
 * from the Repair overview board so the mechanic sees it on the touch queue.
 *
 * ## The note is OPTIONAL, and the flag is the payload
 *
 * The thing being sent is "look at this one"; the sentence is a courtesy. A
 * required note would make the fastest action on the board the one that takes
 * longest, and would collect "please check" — which is what the flag already
 * says. 280 characters because this is a nudge rendered in a queue row, not the
 * comment stream: anything that needs a paragraph belongs in a comment, which
 * already exists and is already visible on the same screen.
 *
 * ## Clearing does NOT go through this body
 *
 * `POST /work-orders/:id/attention/clear` takes `clearWorkOrderAttentionBody`.
 * Overloading this one with a nullable note would make "flag with no note" and
 * "unflag" the same request, and the second is the destructive one.
 */
export const flagWorkOrderAttentionBody = strictObject({
  note: z.string().trim().min(1).max(280).optional(),
  idempotencyKey,
});
export type FlagWorkOrderAttentionBody = z.infer<typeof flagWorkOrderAttentionBody>;

/**
 * Lowering the flag — `POST /work-orders/:id/attention/clear`.
 *
 * A POST to a sub-path rather than a DELETE, which is this repo's idiom for
 * undoing a state: `/assignments/:id/unassign`, `/documents/:id/detach` and the
 * stoppage resume all take this shape, and there is no `app.delete` anywhere in
 * the route surface. It also keeps the idempotency key in a body, which a DELETE
 * cannot carry portably.
 *
 * NO REASON FIELD. Unflagging says "this no longer needs attention", and the
 * record of who lowered it and when is in `audit_event` where every other
 * reversal on this table keeps it.
 */
export const clearWorkOrderAttentionBody = strictObject({
  idempotencyKey,
});
export type ClearWorkOrderAttentionBody = z.infer<typeof clearWorkOrderAttentionBody>;

/**
 * Marking an in-house job "No parts used" — `POST /work-orders/:id/no-parts-used`
 * (TRBUD-300).
 *
 * No other field, on purpose. The choice is a single fact — nothing from stock,
 * no receipt — and who made it and when are stamped by the server from the
 * session, never taken from the client. Clearing is its own route with its own
 * body, the attention flag's shape, so marking and clearing can never be the
 * same request.
 */
export const markNoPartsUsedBody = strictObject({
  idempotencyKey,
});
export type MarkNoPartsUsedBody = z.infer<typeof markNoPartsUsedBody>;

/** Withdrawing the choice before close — `POST /work-orders/:id/no-parts-used/clear`. */
export const clearNoPartsUsedBody = strictObject({
  idempotencyKey,
});
export type ClearNoPartsUsedBody = z.infer<typeof clearNoPartsUsedBody>;

export const postCommentBody = strictObject({
  body: z.string().trim().min(1).max(4000),
  visibility: z.enum(visibilities).default("internal_only"),
  idempotencyKey,
});
export type PostCommentBody = z.infer<typeof postCommentBody>;

export const logTimeBody = strictObject({
  timeKind: z.enum(timeKinds).default("repair_labour"),
  minutes: z.number().int().positive().max(24 * 60),
  workedAt: z.string().datetime({ offset: true }),
  userId: z.string().uuid().optional(),
  notes: z.string().trim().max(1000).optional(),
  idempotencyKey,
});
export type LogTimeBody = z.infer<typeof logTimeBody>;

export const addCostBody = strictObject({
  kind: z.enum(addCostKinds),
  /**
   * Always the magnitude, always positive. `cost_entry_sign_chk` requires
   * recovery kinds to carry a negative amount, and the API applies that sign
   * from `kind` — a client that could send its own sign could post a charge
   * that reads as a credit.
   */
  amount: positiveDecimalString,
  currency: currencyCodeDefaulted,
  note: z.string().trim().max(1000).optional(),
  vendorId: z.string().uuid().nullish(),
  partId: z.string().uuid().nullish(),
  quantity: decimalString.nullish(),
  unitCost: decimalString.nullish(),
  /** The day as typed before day sources existed; recorded with no source. Kept for older clients. */
  effectiveDate: isoDate.nullish(),
  /**
   * TRBUD-458: the day the work or part use happened. Present means the day is
   * recorded as a service day; an explicit null records that it is unknown.
   * Send this or `effectiveDate`, never both.
   */
  serviceDate: boundedDateString("recorded").nullish(),
  idempotencyKey,
});
export type AddCostBody = z.infer<typeof addCostBody>;

export const reverseCostBody = strictObject({
  /**
   * No amount. `reverse_cost_entry` derives the compensating amount from the
   * original entry — the UI must never offer a free-typed figure.
   */
  reason: z.string().trim().min(3).max(1000),
  idempotencyKey,
});
export type ReverseCostBody = z.infer<typeof reverseCostBody>;

export const logRequestBody = strictObject({
  assetId: z.string().uuid(),
  title: z.string().trim().min(1).max(500),
  description: z.string().trim().max(4000).optional(),
  priority: z.enum(priorities).default("normal"),
  /**
   * How this request reached the shop.
   *
   * Built from `manuallyLoggedRequestSources`, NOT from `requestSources`:
   * `driver_app` belongs to `POST /requests`, which hard-codes it, and
   * `automation` has no writer anywhere. See that constant for why claiming
   * either from here is a provenance lie rather than a permission question.
   */
  source: z.enum(manuallyLoggedRequestSources).default("phone"),
  reporterName: z.string().trim().max(200).optional(),
  reporterContact: z.string().trim().max(200).optional(),
  downSince: z.string().datetime({ offset: true }).nullish(),
  /**
   * When the work is NEEDED BY — `maintenance_request.due_date` — TRBUD-251.
   *
   * The column has existed since migration `0004`, carries a `tb_app` INSERT and
   * UPDATE grant, and is read back on `RequestDetail`. Until this field it had
   * **no writer anywhere in the repo**, so every request ever raised carried a
   * NULL in it. That is why "decide later" needs no migration: the planned date
   * has had a home the whole time and nothing to put in it.
   *
   * It is the same column the conversion then defaults its work order's
   * `due_date` from, so a plan made in August still reaches the work order
   * raised from it in September without anybody retyping the date.
   */
  dueDate: boundedDateString("scheduled").nullish(),
  /**
   * Why the work is being done, carried from intake — `maintenance_request.reason_type_id`.
   *
   * Offered here for the same reason the create-work-order body offers it: the
   * column has been on the request since migration `0024`, and
   * `POST /requests/:id/convert` already reads it and passes it to
   * `create_work_order`. Without this field a planning screen that shows a fault
   * type picker would silently drop the answer, because the request had no way
   * to carry it and the conversion had nothing to read.
   *
   * Nullish, never required: a DOT inspection planned three weeks out has no
   * fault, and A2 gates CLOSING rather than raising.
   */
  reasonTypeId: z.string().uuid().nullish(),
  /** Client-generated so a retried submit after signal returns cannot duplicate. */
  idempotencyKey,
});
export type LogRequestBody = z.infer<typeof logRequestBody>;

export const driverRequestBody = strictObject({
  assetId: z.string().uuid(),
  description: z.string().trim().min(1).max(4000),
  latitude: coordinateString.nullish(),
  longitude: coordinateString.nullish(),
  /** Whether the driver confirmed or corrected the Samsara-detected unit. */
  unitConfirmed: z.boolean(),
  idempotencyKey,
});
export type DriverRequestBody = z.infer<typeof driverRequestBody>;

export const convertRequestBody = strictObject({
  typeId: z.string().uuid(),
  priority: z.enum(priorities).default("normal"),
  /**
   * OMITTED and explicit `null` mean different things here — TRBUD-251.
   *
   * `.nullish()` keeps the two apart, and the route relies on it: an **absent**
   * key inherits the request's own `due_date` (the needed-by date somebody set
   * when they planned it), while an explicit `null` clears it. A `.optional()`
   * or a `.default(null)` would collapse both onto "no due date" and silently
   * throw away the plan every time a caller left the field alone — which is
   * every existing caller, since nothing sent this field before.
   */
  dueDate: boundedDateString("scheduled").nullish(),
  parentWorkOrderId: z.string().uuid().nullish(),
  /** "Roadside breakdown" at conversion — TRBUD-409. As on `createWorkOrderBody`. */
  isRoadside: z.boolean().optional(),
  idempotencyKey,
});
export type ConvertRequestBody = z.infer<typeof convertRequestBody>;

export const attachWorkOrderBody = strictObject({
  parentWorkOrderId: z.string().uuid(),
  idempotencyKey,
});
export type AttachWorkOrderBody = z.infer<typeof attachWorkOrderBody>;

export const declineRequestBody = strictObject({
  declineReason: z.string().trim().min(3).max(1000),
  idempotencyKey,
});
export type DeclineRequestBody = z.infer<typeof declineRequestBody>;

export const markAssetDownBody = strictObject({
  reason: z.enum(downtimeReasons),
  workOrderId: z.string().uuid().nullish(),
  downSince: z.string().datetime({ offset: true }).optional(),
  idempotencyKey,
});
export type MarkAssetDownBody = z.infer<typeof markAssetDownBody>;

/**
 * A new asset. `assetClass` is the only classification the database enforces;
 * `assetType` is the free-text detail beside it ("Sprinter Van", "53 ft Plated
 * Trailer") and is optional because it is frequently not known at intake.
 *
 * `unitNumber` is required even though the column is nullable. The column has to
 * stay nullable for the identity projection, which deliberately publishes NOTHING
 * for a unit number its sources disagree about rather than coin-flipping between
 * them. A HUMAN typing a new asset into a form has no such excuse, and an asset
 * with no unit number cannot be found on the Assets screen, whose search matches
 * unit number, name and VIN.
 */
export const createAssetBody = strictObject({
  unitNumber: z.string().trim().min(1).max(64),
  assetClass: z.enum(assetClasses),
  assetType: z.string().trim().max(120).optional(),
  name: z.string().trim().max(120).optional(),
  vin: z.string().trim().length(17).optional(),
  make: z.string().trim().max(80).optional(),
  model: z.string().trim().max(80).optional(),
  modelYear: z.number().int().min(1900).max(2100).optional(),
  ownership: z.enum(ownerships).optional(),
  idempotencyKey,
});
export type CreateAssetBody = z.infer<typeof createAssetBody>;

/** `asset_downtime_release_note_chk`'s upper bound. Declared before the schema that reads it at load. */
export const RELEASE_NOTE_MAX = 1000;

export const backInServiceBody = strictObject({
  /**
   * Role-gated override for the Sylectus on-load guard, with a mandatory
   * reason and an audit event. The guard fails closed; this is the documented
   * recovery path so an outage cannot strand operations.
   */
  overrideOnLoadGuard: z.boolean().default(false),
  overrideReason: z.string().trim().max(1000).optional(),
  /**
   * Why the truck goes back to work while jobs still hold it (TRBUD-346), for
   * example "minor; back Friday for the rest". Required by the route only then;
   * stored on the interval as `end_note`.
   */
  releaseNote: z.string().trim().max(RELEASE_NOTE_MAX).optional(),
  idempotencyKey,
});
export type BackInServiceBody = z.infer<typeof backInServiceBody>;

/**
 * Correcting a downtime interval that was opened wrongly — F-22 / TRBUD-109.
 *
 * `asset_downtime` had two writers and neither could fix a mistake: the mark-down
 * INSERT, and a back-in-service UPDATE whose only statement is
 * `SET ended_at = now()` — the new end is not even a parameter. So a mechanic who
 * marked the WRONG unit down at 09:00 left fabricated hours on it for ever,
 * feeding the Assets screen's "Downtime 30d" column, the Reports chart and the
 * availability KPI, with a DBA statement as the only remedy.
 *
 * ── `replacement: null` means VOID, and it is explicit on purpose ────
 * The three corrections people actually need are "it started later than that",
 * "it ended earlier than that", and "that unit was never off road" — and the
 * third is the case the ticket opens with. A shape where the void is INFERRED
 * from a missing start could not tell "remove it" apart from "leave it alone",
 * the same reason `setRootCauseBody` is `nullable` rather than `optional`.
 *
 * ── The asset is NOT a field here ───────────────────────────────────
 * Moving an interval to a different unit is two operations — void this one, mark
 * that one down — and each is separately auditable. The replacement inherits the
 * asset and the work order from the interval it supersedes.
 *
 * `correctionReason` is required and is NOT the downtime reason. It is why the
 * recorded interval was wrong, and it is what makes a rewritten availability
 * figure auditable rather than merely different. `min(3)` matches
 * `declineRequestBody`; the database re-checks it, and a CHECK constraint holds
 * the column to it as well.
 */
export const correctDowntimeBody = strictObject({
  replacement: z
    .object({
      startedAt: z.string().datetime({ offset: true }),
      /** Null leaves the corrected interval OPEN — the asset is still down. */
      endedAt: z.string().datetime({ offset: true }).nullable(),
      reason: z.enum(downtimeReasons),
    })
    .nullable(),
  correctionReason: z.string().trim().min(3).max(1000),
  idempotencyKey,
});
export type CorrectDowntimeBody = z.infer<typeof correctDowntimeBody>;

export const setRootCauseBody = strictObject({
  rootCauseId: z.string().uuid().nullable(),
  idempotencyKey,
});
export type SetRootCauseBody = z.infer<typeof setRootCauseBody>;

/**
 * The fault type, settable **after** creation — the half of A2 that a create-only
 * writer would have missed.
 *
 * `builtin:fault_type` is an EXIT gate on the last stage before closing, so the
 * moment it is enforced every work order that predates the field has to be able
 * to acquire one. Without this route the gate would be a trap rather than a
 * requirement: 750 of 750 rows in the dev database carry
 * `reason_type_id IS NULL`, and 55 of those are open. `create_work_order` alone
 * cannot reach any of them.
 *
 * SET-THE-LIST since TRBUD-266, replacing the single-value `setReasonTypeBody`.
 *
 * The whole list every time, like `setWorkOrderCategoriesBody` — not an
 * add-one/remove-one pair. A replace is idempotent and a retried submit after a
 * signal drop cannot double-apply it, whereas "add this fault" retried twice
 * needs the pair unique to save it and "remove this fault" retried twice cannot
 * tell success from a race.
 *
 * An empty array is legitimate and means "no faults", which is the old
 * `reasonTypeId: null` — clearing is a real action ("that was the wrong
 * diagnosis") and the replace expresses it without needing a nullable.
 */

export const setWorkOrderFaultsBody = strictObject({
  reasonTypeIds: z.array(z.string().uuid()).max(MAX_WORK_ORDER_FAULTS),
  idempotencyKey,
});
export type SetWorkOrderFaultsBody = z.infer<typeof setWorkOrderFaultsBody>;

/**
 * The route's own bound on a fault-type name.
 *
 * **`reason_type.name` is unbounded `text`** — this is not mirroring a column
 * limit, which is why it is a named constant rather than a `.max(…)` copied off
 * a schema. 120 matches `createWorkOrderTypeBody.name` and `createAssetBody.name`
 * because a fault type renders in the same one-line `<option>` those do, and a
 * name that does not fit is a name nobody can tell apart from its neighbour in
 * the picker.
 */
export const FAULT_TYPE_NAME_MAX = 120;

/**
 * How deep the fault-type tree may go: a root, or one child of a root.
 *
 * `docs/erd.md` 7.2 describes exactly one level — `Scheduled maintenance` as the
 * parent, `Oil change` / `Filters` / `Tyre rotation` beneath it — and **no
 * constraint enforces it**, so it is a route rule. See
 * `faultTypeNestingRefusal` for why it is a rule at all rather than a
 * preference.
 */
export const FAULT_TYPE_MAX_DEPTH = 2;

/**
 * Add a fault type from the picker that needed it — requirement 11.23's
 * "insertable at point of use".
 *
 * ## Why this exists at all
 *
 * `reason_type` held exactly **one** row (`Scheduled maintenance`, seeded by
 * migration `0024`) and **no route created another**. `tb_app` has held the
 * INSERT privilege the whole time, so — exactly as with `cost_status` and
 * `work_order_assignment` — the grant was never the missing piece. Migration
 * `0070` then made `builtin:fault_type` a real EXIT gate on the pre-close stage
 * of both seeded pipelines, which turns a one-row vocabulary into a gate that
 * labels every closing work order `Scheduled maintenance` whether or not it was
 * preventive. `erd.md` 7.2 notes that is **unrecoverable retroactively**:
 * `v_asset_repeat_failure` needs the fault type captured from work order one.
 *
 * ## Why the body is this small
 *
 * `active` is not a parameter. A fault type is born active; retiring one is a
 * separate decision with its own audit action, and offering the flag at creation
 * would let a picker write a row it then cannot show. `id` is not a parameter
 * either — it is `gen_random_uuid()`, which is what makes
 * `reason_type_not_self_chk` (`parent_id <> id`) unreachable from this path.
 * **Do not add one.** The reachable shape of that hazard is a *cycle*
 * (A → B → A), which the CHECK does not catch and which
 * `faultTypeNestingRefusal` does.
 */
export const createReasonTypeBody = strictObject({
  name: z.string().trim().min(1).max(FAULT_TYPE_NAME_MAX),
  /**
   * Nullish, three-way on purpose: absent and `null` both mean "top level", and
   * the picker sends `null` for the same reason `createWorkOrderBody` does — an
   * explicit "no parent" rather than a key that fell off the payload.
   */
  parentId: z.string().uuid().nullish(),
  description: z.string().trim().max(500).optional(),
  idempotencyKey,
});
export type CreateReasonTypeBody = z.infer<typeof createReasonTypeBody>;

/**
 * Editing a fault type from /config — the half that did not exist.
 *
 * The comment on `createReasonTypeBody` above says `active` is "not a parameter"
 * because "retiring one is a separate decision with its own audit action". That
 * was written as a design intent and stayed one: no route ever accepted the
 * flag, so a fault type could be created from either picker and then never
 * renamed or retired by anybody. This body is that separate decision, and
 * `update_reason_type` is that separate audit action.
 *
 * ## Exactly the three columns `02-grants.sql` permits, and no more
 *
 * `reason_type` is granted UPDATE on `name`, `description` and `active`. Reason
 * 3 of the flat-vocabulary block below applies verbatim: a field here that the
 * grant does not name is a form control that fails with 42501 after the person
 * has filled it in.
 *
 * **`parentId` is therefore absent, and that is load-bearing rather than
 * incidental.** The grant withholds it, so re-parenting is not expressible —
 * which is also what keeps the A → B → A cycle out of this path entirely.
 * `faultTypeNestingRefusal` guards the create route because that is the only
 * place a parent is chosen; there is deliberately no second copy of it here,
 * because there is no parent to check. Retire the type and create it in the
 * right place instead.
 *
 * ## A rename re-checks the uniqueness the name IS
 *
 * This table has no `code`, so unlike `root_cause` and `work_order_category`
 * there is no derived key sitting still while the label moves: the name is half
 * of `UNIQUE NULLS NOT DISTINCT (parent_id, name)`. So a rename can collide
 * where a category rename never could, and the route pre-checks it
 * case-insensitively against the row's own siblings — see the PATCH handler.
 */
export const updateReasonTypeBody = strictObject({
  name: z.string().trim().min(1).max(FAULT_TYPE_NAME_MAX).optional(),
  /**
   * Nullish for the reason `updateRootCauseBody.description` is: COALESCE alone
   * cannot tell "leave this alone" from "empty it", so absent means keep and an
   * explicit null clears.
   */
  description: z.string().trim().max(500).nullish(),
  /**
   * The removal mechanism, and it is GUARDED on the server — the same shape as
   * `updateStatusReasonBody.isActive`, for a sharper reason.
   *
   * `builtin:fault_type` is an EXIT gate on the pre-close stage of both seeded
   * pipelines, satisfied by an EXISTS over `work_order_fault_link`. Meanwhile
   * `PUT /work-orders/:id/faults` refuses an inactive type that is NEW to a work
   * order. Put those together and retiring the last active fault type means no
   * open work order lacking one can ever acquire one, and therefore none of them
   * can ever close. The route refuses that last deactivation by name.
   */
  active: z.boolean().optional(),
  idempotencyKey,
});
export type UpdateReasonTypeBody = z.infer<typeof updateReasonTypeBody>;

/**
 * Whether `parent` may take a child. Returns the refusal sentence, or `null`.
 *
 * Three reasons this is a refusal rather than a shrug:
 *
 * 1. **Nothing renders a deeper tree.** `GET /reason-types` returns a flat
 *    alphabetical list and both pickers are flat `<select>`s, so a grandchild is
 *    structure the product cannot show — it would look like a top-level type
 *    while grouping somewhere invisible.
 * 2. **Fault analysis rolls up one level.** `erd.md` 7.2's filter is "exclude
 *    preventive", which reads the child's parent. An arbitrary-depth tree turns
 *    that into a recursive CTE nobody has written.
 * 3. **It is the cycle guard the database does not have.** `erd.md` 7.2 flags
 *    the missing trigger as a SPEC-Δ: `reason_type_not_self_chk` stops A → A and
 *    not A → B → A. If every parent must itself be a root, a cycle of length ≥ 2
 *    is structurally impossible through this route — a child's parent has no
 *    parent, so nothing can point back. That is a real safety property, not
 *    tidiness, and it is why the rule is enforced rather than warned about.
 */
export function faultTypeNestingRefusal(parent: {
  name: string;
  parentId: string | null;
}): string | null {
  if (parent.parentId === null) return null;
  return (
    `"${parent.name}" is itself nested under another fault type, and the vocabulary is ` +
    "one level deep. Nest this under a top-level fault type, or add it at the top level."
  );
}

/**
 * The sentence a duplicate gets, naming the type that already holds the name.
 *
 * The constraint behind it is `UNIQUE NULLS NOT DISTINCT (parent_id, name)` —
 * note **NULLS NOT DISTINCT**, so two top-level types called "Brakes" collide
 * even though both `parent_id` are NULL, which plain SQL `=` would never find.
 * A bare `23505` maps to "That has already been recorded. Reload to see the
 * current state.", which is true and useless: the person is standing in front of
 * a picker and needs to know the type they just typed is already in it.
 */
export function faultTypeDuplicateMessage(
  existing: { name: string },
  parentName: string | null,
): string {
  return parentName === null
    ? `"${existing.name}" already exists as a top-level fault type — pick it from the list.`
    : `"${existing.name}" already exists under "${parentName}" — pick it from the list.`;
}

/**
 * Assigning a work order — the writer `work_order_assignment` never had.
 *
 * ── What was broken ─────────────────────────────────────────────────
 * The table had **no INSERT anywhere** in `apps/`, `packages/` or `scripts/`,
 * while five separate reads depended on it: `assertAssigned` (the `advanceStages`
 * scope of `"assigned"`), the mechanic's own-work queue filter in two places, the
 * detail header's assignee line, and `v_labour_roi`. So a role scoped to
 * `assigned` could advance nothing, and the mechanic queue filtered against a
 * table that could not have rows. `02-grants.sql` already lists the table as
 * `app_writable`, so — exactly like `cost_status` — the privilege was never the
 * missing piece.
 *
 * ── Exactly one subject ─────────────────────────────────────────────
 * `work_order_assignment_one_subject_chk` is
 * `num_nonnulls(user_id, vendor_contact_id) = 1`, so a row names an internal
 * person **or** a vendor contact, never both and never neither. Requirement 10.2
 * wants all three of processor, mechanic and vendor assignable, which is why the
 * vendor-contact arm exists at all. `superRefine` enforces the same rule here so
 * the caller gets a sentence instead of a `23514`.
 *
 * ── `assignment_role` is deliberately not a parameter ───────────────
 * The column has no CHECK and defaults to `'assignee'`. BUILD_SPEC section 2 is
 * not in this repo, so there is no authority for a wider vocabulary, and
 * inventing one would bake a guess into the unique index
 * `(work_order_id, user_id, assignment_role)`. The API writes the default and
 * nothing else; widening it later is additive.
 */
export const assignWorkOrderBody = strictObject({
    userId: z.string().uuid().nullish(),
    vendorContactId: z.string().uuid().nullish(),
    idempotencyKey,
  })
  .superRefine((value, ctx) => {
    const named = [value.userId, value.vendorContactId].filter(
      (v) => v !== null && v !== undefined,
    ).length;
    if (named !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "an assignment names exactly one of userId or vendorContactId — " +
          "work_order_assignment_one_subject_chk enforces the same rule",
      });
    }
  });
export type AssignWorkOrderBody = z.infer<typeof assignWorkOrderBody>;

export const unassignWorkOrderBody = strictObject({
  idempotencyKey,
});
export type UnassignWorkOrderBody = z.infer<typeof unassignWorkOrderBody>;

/**
 * The cost-status ruling — the writer `cost_status` never had.
 *
 * ── Why this is a human ruling and not a derived value ───────────────
 * The obvious-looking alternative is to have `transition_work_order` set
 * `awaiting_final_cost` whenever a vendor pipeline closes. Note that this is NOT
 * an argument against derived values in general — `0030` on
 * `trbud-doc-rebaseline` has that same function close an open `asset_downtime`
 * range off the stage flags, because completion reliably means the truck is
 * fixed. Billing carries no such implication: a roadside job
 * can close with the invoice already in hand, and an in-house job can close
 * waiting on a machine-shop bill. The stage does not know which; only the person
 * closing it does.
 *
 * ── Why the column existed with nothing writing it ───────────────────
 * `work_order.cost_status` has a CHECK allowing `awaiting_final_cost`,
 * `v_awaiting_final_cost` selects on it, the insights endpoint publishes a tile
 * keyed on it, and `02-grants.sql` grants `tb_app` the column-level UPDATE. Every
 * piece was in place except a route, so the view and the tile were structurally
 * always empty for natively created work. This is that route.
 *
 * `note` is optional and lands in the audit delta rather than on the row: why a
 * job is waiting on a bill is provenance, and `work_order` has no column for it.
 */
export const setCostStatusBody = strictObject({
  costStatus: z.enum(costStatuses),
  note: z.string().trim().max(1000).optional(),
  idempotencyKey,
});
export type SetCostStatusBody = z.infer<typeof setCostStatusBody>;

export const completeProcedureStepBody = strictObject({
  completed: z.boolean(),
  notes: z.string().trim().max(1000).optional(),
  idempotencyKey,
});
export type CompleteProcedureStepBody = z.infer<typeof completeProcedureStepBody>;

export const issuePartBody = strictObject({
  partId: z.string().uuid(),
  workOrderId: z.string().uuid(),
  locationId: z.string().uuid(),
  quantity: positiveDecimalString,
  /**
   * The caller confirms this is new work, on a work order that already carries
   * part costs imported from MaintainX (24 September 2026).
   *
   * Without it the route refuses such a work order with
   * `acknowledge: "imported_parts"`. Work order 12379 is why: MaintainX's own
   * counts became TruckBuddy's opening stock, so a part MaintainX had already
   * used on the job came off the shelf a second time when it was issued here.
   * Imported part lines carry no part id, so the question is asked about the
   * work order, not about the part.
   */
  acknowledgedImportedParts: z.boolean().default(false),
  /**
   * TRBUD-458: the day the part was used on the job. A day records it as the
   * cost's day; an explicit null says it is not known; omitted keeps the old
   * rule (the day the issue was recorded).
   */
  usedOn: boundedDateString("recorded").nullish(),
  idempotencyKey,
});
export type IssuePartBody = z.infer<typeof issuePartBody>;

/**
 * Move stock between two shelves — TRBUD-250 line B.
 *
 * ## `reason` is OPTIONAL, unlike `returnPartBody`'s
 *
 * The asymmetry follows the one that file already records. A return or a
 * reversal says an earlier movement was WRONG, and a correction nobody explained
 * is the thing a later reader cannot reconstruct — so both carry a 3-character
 * floor. A transfer asserts nothing about the past: the van went to the other
 * yard and took the filters with it. Requiring a sentence there buys "moved" and
 * "n/a" typed a hundred times, which is worse than nothing, because it makes the
 * reasons on the rows that DO have one unreadable.
 *
 * ## The same-location refusal is NOT expressed here
 *
 * It could be — `.refine()` reaches both fields. It deliberately is not.
 * `record_inventory_transfer` raises `23514 'transfer locations must differ'`,
 * and that routine is the enforcement for every caller including a script. A
 * second opinion in the schema is the defect the gold tier exists to avoid: the
 * two could disagree, and the schema's would win silently on the one path that
 * has a screen. The ROUTE states it in words, before the write, so the operator
 * gets a sentence rather than a constraint violation — message, not enforcement.
 *
 * `quantity` is a positive decimal STRING for the reason every money and
 * quantity field here is: `inventory_txn.quantity` is `numeric`, and a JSON
 * number cannot carry it back unrounded.
 */
export const transferStockBody = strictObject({
  partId: z.string().uuid(),
  fromLocationId: z.string().uuid(),
  toLocationId: z.string().uuid(),
  quantity: positiveDecimalString,
  reason: z.string().trim().min(1).max(500).optional(),
  idempotencyKey,
});
export type TransferStockBody = z.infer<typeof transferStockBody>;

/**
 * Count what is physically on one shelf — TRBUD-250 line C.
 *
 * ## `countedQuantity` is the one quantity field here that admits ZERO
 *
 * Every other quantity on this surface is `positiveDecimalString` or
 * `quantityString`, because issuing, receiving, returning and transferring
 * nothing are all requests that mean nothing. A count of zero is a REAL finding:
 * the shelf is empty, and saying so is exactly the correction that has never had
 * a path — `record_inventory_txn`'s adjust arm can only ever ADD stock
 * (`quantity > 0` against `v_on_hand := p_quantity`), which is the gap
 * `record_stock_count` exists to close. So this shape is `quantityString`'s
 * regex with a `>= 0` floor rather than `> 0`.
 *
 * The regex is matched to the COLUMN, `stock_count.counted_quantity`
 * `numeric(12,3)`, following the lesson `unitCostString` records above.
 *
 * ## It is an ABSOLUTE figure, not a delta
 *
 * "There are N on this shelf", never "add three". That is what a count IS, and
 * it also makes the request idempotent in the ordinary sense on top of the
 * `idempotencyKey`: sending it twice cannot double-apply, because the second
 * send asks for the same end state. The steppers on the screen nudge the
 * displayed number toward what the person can see; what travels is the number.
 *
 * ## `note` is OPTIONAL, and the asymmetry is the same one `transferStockBody`
 * records
 *
 * A return or a reversal says an earlier movement was WRONG, so both carry a
 * floor. A count asserts nothing about the past — somebody looked at a shelf.
 * Requiring a sentence would buy "count" and "n/a" typed a hundred times, which
 * makes the notes on the rows that do carry one unreadable. The arithmetic is
 * recorded regardless: the routine builds the ledger row's reason from the
 * counted and expected figures and appends the note rather than replacing it.
 */
export const recordStockCountBody = strictObject({
  locationId: z.string().uuid(),
  countedQuantity: z
    .string()
    .trim()
    .regex(/^\d{1,9}(\.\d{1,3})?$/, "expected a quantity with at most 3 decimal places")
    .refine((v) => Number.parseFloat(v) >= 0, "a counted quantity cannot be negative"),
  note: z.string().trim().min(1).max(500).optional(),
  idempotencyKey,
});
export type RecordStockCountBody = z.infer<typeof recordStockCountBody>;

export const reviewLineDecisionBody = strictObject({
    // `proposed_cost_line.line_number` is an `integer`, so its type bounds it.
    lineNumber: z.number().int().positive().max(INT4_MAX),
    decision: z.enum(["accept", "reject", "correct"]),
    /**
     * Required only on `correct`.
     *
     * On the DEMO path it is stored as a labelled parser training example. On the
     * MANUAL path (TRBUD-162) it IS the edit: the fields named here are written
     * back to the `proposed_cost_line` row, which is how a person fixes a
     * mistyped figure without re-logging the whole invoice —
     * `vendor_invoice_vendor_number_uq` would refuse the second log.
     *
     * `kind` was added for that path. Without it a wrongly-classified line had
     * no repair at all: rejecting it drops the line from the invoice, and
     * re-logging is refused by the invoice-number unique. Optional, so the demo
     * path is unchanged and simply never sends one.
     *
     * `quantity` and `unitCost` deliberately KEEP `decimalString` rather than
     * moving to `quantityString` / `unitCostString`. These values are also
     * written verbatim into `parser_feedback.corrected_value` as jsonb, where a
     * tightened regex would start refusing shapes the table already holds; the
     * manual writer narrows them on the way to the column instead.
     */
    correction: z
      .object({
        description: z.string().trim().max(500).optional(),
        serviceDate: boundedDateString("recorded").nullish(),
        kind: z.enum(invoiceLineKinds).optional(),
        quantity: decimalString.nullish(),
        unitCost: decimalString.nullish(),
        amount: decimalString.optional(),
      })
      .optional(),
    targetWorkOrderId: z.string().uuid().nullish(),
    idempotencyKey,
  })
  .superRefine((value, ctx) => {
    if (value.decision === "correct" && value.correction === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["correction"],
        message: "A corrected decision needs the corrected values.",
      });
    }
    if (value.decision !== "correct" && value.correction !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["correction"],
        message: "Only a corrected decision may carry corrected values.",
      });
    }
  });
export type ReviewLineDecisionBody = z.infer<typeof reviewLineDecisionBody>;

/**
 * Confirm a reviewed invoice and post its lines.
 *
 * `workOrderId` stays REQUIRED even for a manual item that already knows its own
 * work order, and the server CHECKS the two agree rather than trusting either.
 * Dropping it would be the smaller change and the wrong one: this is the request
 * that writes to the ledger, `cost_entry` grants no DELETE to anybody, and a
 * client that has drifted onto a different work order should be refused rather
 * than quietly corrected. H2 is why it is per-invoice and not per-line — a real
 * statement invoice spans several units and `cost_entry.work_order_id` is NOT
 * NULL, so per-line assignment has to land before this handles vendor mail.
 *
 * `.strict()` since TRBUD-162: this body reaches money routines, and an ignored
 * extra field is how a client comes to believe it sent something that was never
 * read.
 */
/**
 * Confirm a logged invoice: name what it was logged against, exactly one of the
 * two. The server checks the name against the item's own header and refuses a
 * mismatch, so a client that has drifted onto another job or order cannot post.
 */
export const confirmInvoiceBody = strictObject({
    sameAmountReceiptAcknowledged: z.boolean().optional(),
    existingCostDecision: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("additional") }).strict(),
      z.object({ kind: z.literal("replaces_import"), costEntryId: z.string().regex(/^[1-9][0-9]*$/) }).strict(),
    ]).optional(),
    workOrderId: z.string().uuid().optional(),
    /** A bill on a purchase order (`createPurchaseOrderBillBody`). */
    purchaseOrderId: z.string().uuid().optional(),
    idempotencyKey,
  })
  .strict()
  .refine((body) => (body.workOrderId === undefined) !== (body.purchaseOrderId === undefined), {
    message: "Name the work order or the purchase order this invoice was logged against, not both.",
    path: ["workOrderId"],
  });
export type ConfirmInvoiceBody = z.infer<typeof confirmInvoiceBody>;

/**
 * Receive stock onto a shelf.
 *
 * This is the missing half of the parts flow. `record_inventory_txn` requires a
 * `location_id` for every movement, so a part that has never been received
 * anywhere has no shelf to be issued *from* — and until this existed the Issue
 * part dialog was a permanent dead end for exactly those parts, with no route out
 * of it anywhere in the UI.
 *
 * No unit cost is taken. `part` has **no unit-cost column** (H1) and there is no
 * stated valuation policy — last cost, moving average, standard — so a figure
 * captured here would be an invention that later reads as a valuation. Receiving
 * moves quantity only; cost attribution happens when the part is issued.
 */
export const receivePartBody = strictObject({
  partId: z.string().uuid(),
  locationId: z.string().uuid(),
  quantity: positiveDecimalString,
  /** Free text — a PO number, a packing slip, whoever dropped it off. */
  note: z.string().trim().max(500).optional(),
  idempotencyKey,
});
export type ReceivePartBody = z.infer<typeof receivePartBody>;

/**
 * Return a previously issued part to the shelf it came off. TRBUD-124 / F-33.
 *
 * **`inventoryTxnId` is the original ISSUE, and there is deliberately no
 * partId / locationId / workOrderId here.** All three are copied from that row
 * by `return_part_to_stock`, so the stock goes back where it came from,
 * attributed to the work order it left for. Accepting them from the caller
 * would make this a transfer wearing a return's name, and would let a return
 * put stock on a shelf it never left.
 *
 * It is a **string** rather than a number because `inventory_txn.id` is a
 * bigint — a JSON number would lose precision, and `partsQuery`'s siblings
 * already treat ledger ids as strings on the way out.
 *
 * **`reason` is required, unlike the optional `note` on receiving above**, and
 * the asymmetry is the point. Receiving records goods arriving, which the
 * paperwork explains; a return says an earlier movement was wrong, and a stock
 * correction nobody explained is the one thing a later reader cannot
 * reconstruct. The 3-character floor matches `reverseCostBody`, the other
 * "somebody made a mistake" path, so the two corrections hold the same line.
 *
 * `quantity` may be less than the original — 3 of 4 came back and one was
 * destroyed — but `inventory_txn_reverses_uq` allows only ONE return per issue,
 * so there is no second bite. The route says so before the user commits.
 */
export const returnPartBody = strictObject({
    inventoryTxnId: z
      .string()
      .trim()
      .regex(/^[1-9][0-9]{0,18}$/, { message: "That is not an inventory transaction id." })
      // Nineteen digits is not a bound on its own: 9999999999999999999 matches
      // and is past the largest bigint. `isBigintId`, never a bare `BigInt()`:
      // zod 4 runs a refine even after the regex above has failed, so a bare
      // conversion threw on "4.5" and turned a refusal into a 500.
      .refine(isBigintId, { message: "That is not an inventory transaction id." }),
    quantity: positiveDecimalString,
    reason: z.string().trim().min(3).max(500),
    idempotencyKey,
  })
  .strict();
export type ReturnPartBody = z.infer<typeof returnPartBody>;

export const publishConfigBody = strictObject({
  idempotencyKey,
});
export type PublishConfigBody = z.infer<typeof publishConfigBody>;

/**
 * Create a work-order type, together with a usable pipeline.
 *
 * **`startFrom` is required and has no "none" option, and that is the point.**
 * `work_order.current_stage_id` is NOT NULL and `create_work_order` raises
 * *'work-order type has no active entry stage'* against an empty
 * `work_order_stage`, so a type created without stages is a type nothing can ever
 * open. Making the pipeline choice part of the request means the API can write the
 * type, its stages and its transitions in one transaction and never leave a
 * half-built type behind.
 *
 * `code` is constrained to a slug because `work_order_type_code_uq` is unique on
 * `lower(code)` and the value shows up in URLs and seed comparisons; the display
 * name is the free-text field.
 */
export const createWorkOrderTypeBody = strictObject({
  code: z
    .string()
    .trim()
    .min(2)
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, "lower-case letters, digits and underscores, starting with a letter"),
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).optional(),
  startFrom: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("minimal") }),
    z.object({ kind: z.literal("copy"), sourceTypeId: z.string().uuid() }),
  ]),
  idempotencyKey,
});
export type CreateWorkOrderTypeBody = z.infer<typeof createWorkOrderTypeBody>;

/**
 * The longest custom-field key a person can create.
 *
 * `custom_field_def.key` is `text` with a shape CHECK and no length, so the
 * database sets no limit; this is the bound the Add a custom field dialog has
 * always typed to. The body carried none of its own, so a request could store a
 * key of any length that no screen was built to show. The dialog reads this
 * constant rather than restating 60, so the two cannot drift.
 */
export const CUSTOM_FIELD_KEY_MAX = 60;

export const addCustomFieldBody = strictObject({
  key: z
    .string()
    .trim()
    .max(CUSTOM_FIELD_KEY_MAX)
    .regex(/^[a-z][a-z0-9_]*$/, "keys are lowercase, start with a letter, and cannot contain ':'"),
  label: z.string().trim().min(1).max(200),
  dataType: z.enum(customFieldDataTypes),
  currencyCode: currencyCode.nullish(),
  isRequired: z.boolean().default(false),
  idempotencyKey,
});
export type AddCustomFieldBody = z.infer<typeof addCustomFieldBody>;

/**
 * The work-order queue's filters (F-37 for the category half).
 *
 * ## `.strict()`, and it is the more important half of this change
 *
 * A zod object strips unknown keys by default, so `parseQuery` used to DISCARD an
 * unrecognised filter without a word. Measured: exactly one work order carried
 * the 'Damage' category, `GET /api/work-orders` returned 26, and `?categoryId=`,
 * `?categoryIds=` and `?category=` each returned **26** with a 200. A caller got
 * a full fleet list that looked like a successful filter matching everything —
 * worse than a 400, because nothing about the response says the filter did not
 * run. Strict turns each of those into a readable `Unrecognized key:
 * "categoryId"`.
 *
 * ## `categoryIds` is a COMMA-SEPARATED STRING, not an array
 *
 * About the transport rather than taste: `buildUrl` in `apps/web/src/lib/api.ts`
 * sets a search parameter only for a string, number or boolean and skips anything
 * else *silently*. An array-shaped parameter would be dropped by the client
 * before it ever reached the server — the same silent failure this change exists
 * to end, moved one layer earlier.
 *
 * Several ids mean "in ANY of these", matching how the picker reads: a work order
 * tagged `Damage` shows under a `Damage, Warranty` filter.
 */
const categoryIdList = z
  .string()
  .trim()
  .min(1)
  .max(800)
  .transform((value) =>
    value
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part !== ""),
  )
  // Piped rather than checked inside the transform, so a bad id is an
  // `invalid_format` issue naming its position instead of a thrown string.
  .pipe(z.array(z.string().uuid()).min(1).max(20))
  .optional();

export const queueQuery = z
  .object({
    q: z.string().trim().max(200).optional(),
    stageId: z.string().uuid().optional(),
    typeId: z.string().uuid().optional(),
    repairKind: z.enum(["INTERNAL", "VENDOR"]).optional(),
    priority: z.enum(priorities).optional(),
    assigneeId: z.string().uuid().optional(),
    categoryIds: categoryIdList,
    /**
     * One vendor — TRBUD-441, Brett's 20 October note 4: *"Need all visuals to
     * be filterable by work category, vendor"*.
     *
     * A work order matches when the vendor is its primary vendor or a live
     * (not removed) additional vendor, and an itemized ticket matches through
     * any live package that does. That rule lives in `queueScopeCandidates`
     * (`apps/api/src/read/work-orders.ts`), the one the Repair overview board
     * already used; the queue now reaches it too.
     *
     * Before this key the queue answered `?vendorId=` with a 400, which is the
     * strictness doing its job: the server could filter by vendor and nothing
     * here let a caller ask.
     */
    vendorId: z.string().uuid().optional(),
    overdueOnly: queryBoolean.optional(),
    writable: queryBoolean.optional(),
    /**
     * Which of the queue's views to read — TRBUD-216, the "completed/Cancelled
     * WO tab".
     *
     * The vocabulary, the boundary it draws and why `is_completed` rather than
     * `is_closed` are all documented on `queueStatusViews` in `vocabulary.ts`.
     * Two things belong here rather than there:
     *
     * **`.default("open")` changes what an existing caller gets**, and that is
     * intended for the queue and NOT for the four work-order pickers
     * (`IssuePartDialog`, `MarkAssetDownDialog`, `NewPurchaseOrderDialog`,
     * `StartStoppageDialog`), which read the same route. They pass
     * `status: "unclosed"` — today's boundary, written down — so the narrowing
     * reaches the queue and nothing else. A silent default change in a money
     * flow is the failure this avoids.
     *
     * **It is an enum, not two booleans.** `includeCompleted` +
     * `includeCancelled` would make "completed only" inexpressible and
     * "cancelled and completed but not open" expressible, and the screen has a
     * tab bar: one of three, always exactly one.
     */
    status: z.enum(queueStatusViews).default("open"),
    /**
     * Due on or before this date — the due-date filter (TRBUD-223).
     *
     * Eden, 9 September 2026: *"The due date filter is not working, that's on
     * me."* What the screen offered was an "Overdue only" checkbox and nothing
     * else, and that checkbox WAS applied by the server (proved, not assumed, in
     * `apps/api/src/routes/queue-order-and-filters.dbtest.test.ts`) — so the
     * missing thing was never a broken filter, it was the absence of a filter
     * that takes a date. This is it.
     *
     * INCLUSIVE of the day named — `<=`, not `<`. "Due on or before the 10th"
     * with the 10th excluded is the off-by-one everybody reports as broken, and
     * `overdueOnly` already owns the exclusive `< today` comparison.
     *
     * A work order with NO due date matches neither this nor `dueOn`: it is not
     * due before anything. Asserted rather than implied, because the alternative
     * reading — undated work is not yet ruled out, so keep it — is defensible and
     * would make the filter look like it did nothing on a fleet where most due
     * dates are unset.
     */
    dueBefore: calendarDate.optional(),
    /** Due on exactly this day. Same undated rule as `dueBefore`. */
    dueOn: calendarDate.optional(),
    /**
     * Show cancelled work orders as well as open ones — the read half of Eden's
     * "delete button", ruled 8 September 2026.
     *
     * **Still here, and still the flag `?includeCancelled=true` has always
     * been**, but it is now an OVERLAY on the `open` view rather than the only
     * way to a cancelled work order. `status=cancelled` is the view; this puts
     * cancelled rows back alongside open ones. It is ignored on the other views,
     * where it would be either redundant (`cancelled`) or meaningless
     * (`completed`) — which is what keeps "one way to see cancelled work per
     * view" true and avoids the two-controls-one-job confusion Eden objected to
     * on 8 September (*"why is there two tabs. this is confusing."*).
     *
     * ## What it actually widens, which is NOT what the name suggests
     *
     * The queue's base predicate is `NOT stage.is_closed`, and a cancellation is
     * a transition into a stage carrying `is_closed`. So a cancelled work order
     * already left the list the moment it was cancelled, along with every
     * completed one — the default this flag names was never written for
     * cancellations and is not being added here. What `includeCancelled=true`
     * does is put the CANCELLED ones back, and only those: it is
     * `NOT s.is_closed OR s.code = 'cancelled'`, never "show everything closed".
     *
     * That asymmetry is the ruling: "make the status as cancelled, remove it
     * from the WO page. I want it still pullable" — gone from the working list
     * by default, reachable on purpose, and never destroyed.
     *
     * ## `queryBoolean`, not `z.coerce.boolean()`
     *
     * TRBUD-172's lesson, and it bites hardest on a flag whose false value is
     * the one people spell out: `z.coerce.boolean()` reads the STRING "false" as
     * `true`, because every non-empty string is truthy. A UI that sets
     * `includeCancelled=false` to mean "hide them" would have shown them.
     *
     * `.default(false)` rather than `.optional()` so the parsed query carries a
     * real boolean — `readQueueGrouped` reads the key's presence, and an
     * `undefined` there would be one more thing for a caller to narrow.
     */
    includeCancelled: queryBoolean.default(false),
    /**
     * "Waiting on me" — and it means a DIFFERENT thing per role, which is why it
     * is a boolean and not a user id.
     *
     * The server resolves it from `session.role` and `session.userId`; the client
     * never sends an id for it. For a `mechanic` it is "assigned to me". For
     * `admin` / `manager` / `processor` it is "work waiting on a manager's
     * action": a ticket with unrouted issues, a repair with nobody on it, or a
     * vendor job on one of the stages the manager drives. For `requester` and
     * `viewer` it is IGNORED — a requester is already scoped to its own rows and
     * a viewer has no queue of its own, so honouring it there would return an
     * empty list that reads as a fault.
     *
     * ── `.optional()`, not `.default(...)`, and the default lives on the CLIENT
     * `queueQuery.status` records the same reasoning: this route is also read by
     * the four work-order pickers, and a server-side default would narrow THEM.
     * `useQueueGrouped` has exactly one caller, so the manager screen's
     * "waiting on me by default" cannot leak anywhere else.
     *
     * ── It AND-s with `assigneeId` rather than replacing it ──────────
     * The two are independent filters and a request carrying both narrows to the
     * intersection: "unassigned work" and "assigned to Dana" together is an
     * empty list, and that is the honest answer rather than a silently dropped
     * parameter.
     *
     * `queryBoolean` for the reason `includeCancelled` gives at length:
     * `z.coerce.boolean()` reads the STRING "false" as `true`, so a client
     * turning the filter off would have turned it on.
     */
    mine: queryBoolean.optional(),
    /**
     * TRBUD-401: the set the "Awaiting final cost" figure counts. The repair is
     * finished, the work order is not closed or cancelled, and its cost status
     * is still awaiting the final cost. ONE predicate with the count
     * (`awaitingPaperworkPredicate`), inside the queue's own scope. Meaningful
     * on the Completed view, where every such work order is.
     */
    costStatus: z.enum(["awaiting_final_cost"]).optional(),
    /**
     * The column order — TRBUD-391. `lastUpdate:desc,due:asc`: tiers in click
     * order, the first deciding and each later one breaking ties in the ones
     * before it. Absent is the default order (asset bands, best priority, then
     * earliest due date). See `queue-sort.ts` for the keys and `queueOrderBy` in
     * `apps/api/src/read/work-orders.ts` for the SQL.
     *
     * Not a filter: it chooses the order and leaves every count alone. A string
     * in the URL for the reason `categoryIds` is one.
     */
    sort: queueSortParam.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .strict();
export type QueueQuery = z.infer<typeof queueQuery>;

/** `.extend()` preserves `.strict()` — asserted in `queue-query.test.ts`, not assumed. */
export const queueGroupsQuery = queueQuery.extend({
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type QueueGroupsQuery = z.infer<typeof queueGroupsQuery>;

/**
 * Who may be assigned a work order — the LAST unvalidated query read (TRBUD-176).
 *
 * ## What it replaces
 *
 * `GET /assignable-parties` read its one parameter with a bare cast:
 *
 *     typeof (request.query as { workOrderId?: unknown })?.workOrderId === "string"
 *       ? ... : null
 *
 * TRBUD-152 made every other query schema `.strict()` and left this one alone,
 * recording the reason in `docs/plans/trbud-152-strict-query-schemas.md`: giving
 * it a schema is a behaviour CHANGE, because a malformed value currently degrades
 * to "offer everyone" and now becomes a 400. That trade is taken here — an
 * unfiltered roster is precisely the wrong answer, since the filter exists to stop
 * a mechanic being assigned a Roadside job they cannot advance a single stage of.
 *
 * ## `.optional()`, because absence is a real and supported request
 *
 * The route reads `null` as "do not narrow", and `useAssignableParties()` omits
 * the parameter whenever it is opening the picker outside a work order. Making it
 * required would 400 that call. What is NOT supported is a present-but-unusable
 * value: `${workOrderId}::uuid` in the SQL would raise `22P02` on a non-uuid, so
 * the choice was never between validating and not — only between a 400 that names
 * the parameter and a 500 that names nothing.
 */
export const assignablePartiesQuery = z
  .object({
    workOrderId: z.string().uuid().optional(),
  })
  .strict();
export type AssignablePartiesQuery = z.infer<typeof assignablePartiesQuery>;

/**
 * The asset register's filters.
 *
 * `.strict()` per TRBUD-152, and the pairs below are why it matters more here
 * than anywhere else in this file: `assetType` and `assetClass` are two
 * different filters over two different things, and `unclassified` is a value one
 * of them accepts and the other never holds. So `?assetClasses=`, `?class=` and
 * `?type=` are all plausible, all wrong, and were all discarded in favour of a
 * 200 listing the whole fleet up to the 100-row default — which on a register
 * screen reads as "your filter matched everything" rather than as a fault.
 */
export const assetsQuery = z
  .object({
    q: z.string().trim().max(200).optional(),
    assetType: z.string().trim().max(100).optional(),
    location: z.string().trim().max(200).optional(),
    status: z.enum(["in_service", "down", "testing"]).optional(),
    /**
     * TRBUD-401: the units the "Assets down" figure counts, by its own rule
     * (`assetAvailability`): any open downtime interval, post-repair testing
     * INCLUDED, retired and disposed left out. Not the same set as
     * `status=down`, which leaves testing out, and deliberately a second key
     * rather than a change to that one (Eden, 22 October 2026).
     */
    availability: z.enum(["down"]).optional(),
    /**
     * The Trucks / Trailers / Other / Unclassified tab.
     *
     * Distinct from `assetType`, which filters the free-text detail verbatim. This
     * one selects a fleet segment; the two are not interchangeable and both are
     * useful. Omitted means all classes, which is what the "All" tab sends.
     *
     * **The FILTER vocabulary, not the stored one**, and that difference is load
     * bearing: `unclassified` is a tab the screen offers but never a value
     * `asset_class` holds. Validating against `assetClasses` here would 400 the
     * Unclassified tab -- which it briefly did, because the column landed before
     * this line was updated.
     */
    assetClass: z.enum(assetClassFilters).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .strict();
export type AssetsQuery = z.infer<typeof assetsQuery>;

/**
 * The vendor list's filters and sort.
 *
 * `.strict()` per TRBUD-152. The near-miss that matters here is `?state=TX`
 * instead of `?region=TX`: "state" is what most of the United States calls the
 * field, so it is the spelling a user reaches for first, and it was dropped in
 * favour of a 200 listing all 85 vendors. The location filter is the half of
 * TRBUD-82 management actually asked for, and a filter that appears to work and
 * silently does not is worse than one that is missing.
 */
export const vendorsQuery = z
  .object({
    q: z.string().trim().max(200).optional(),
    vendorType: z.enum(["repair", "parts", "external"]).optional(),
    status: z.enum(["APPROVED", "CONDITIONAL", "INACTIVE", "BLACKLISTED"]).optional(),
    capability: z.string().trim().max(100).optional(),
    minRating: z.coerce.number().min(0).max(5).optional(),
    /*
     * ── Filter and sort by location (TRBUD-82) ───────────────────────
     *
     * Management's original ask, and the half of the ticket that has to work with
     * ZERO coordinates — which is the state of all 85 vendors today. So these key
     * off `vendor.city` / `vendor.region`, real text columns a human fills in,
     * and not off latitude/longitude.
     *
     * Note these filter STORED columns, unlike `capability` and `minRating`, which
     * filter values the demo overlay invents. That difference is surfaced on the
     * screen rather than left for a reader to infer: a filter on a real column and
     * a filter on a fabricated one behaving identically is how somebody comes to
     * trust the fabricated one.
     */
    city: z.string().trim().max(200).optional(),
    region: z.string().trim().max(100).optional(),
    /**
     * `location` orders by city then region then name, putting vendors with no
     * city LAST rather than first.
     *
     * Ascending text order would sort NULL either first or last depending on the
     * collation and the driver, and "every unplaced vendor at the top" makes the
     * sort look broken to the one user who most needs it. Unplaced vendors are
     * not excluded, though — hiding 85 of 85 rows behind a sort control would be
     * a worse lie than showing them at the bottom.
     */
    sort: z.enum(["name", "location"]).optional(),
    /*
     * ── The vendors missing one field (TRBUD-466) ─────────────────────
     *
     * Opened from the data completeness page. It narrows to the vendors in use
     * (active and conditional) for which `fieldState` says the field is MISSING,
     * the same rule the page counts with, so a row there and this list cannot
     * disagree. `includeRetired` adds inactive and blacklisted vendors, as the
     * page's tick-box does.
     */
    missing: z.enum(vendorCompletenessFields).optional(),
    includeRetired: queryBoolean.optional(),
  })
  .strict()
  /*
   * `includeRetired` only means something beside `missing`: the plain list
   * already shows every vendor, retired ones included. Accepting it alone would
   * answer 200 and ignore it, which is the F-37 failure `queryBoolean`'s note
   * describes, so it is refused.
   */
  .refine((value) => value.includeRetired === undefined || value.missing !== undefined, {
    message: "includeRetired only applies with missing: the full vendor list already includes retired vendors.",
    path: ["includeRetired"],
  });
export type VendorsQuery = z.infer<typeof vendorsQuery>;

/**
 * `GET /vendors/completeness` (TRBUD-466). Active and conditional vendors by
 * default; `includeRetired` adds inactive and blacklisted ones.
 */
export const vendorCompletenessQuery = z
  .object({ includeRetired: queryBoolean.default(false) })
  .strict();
export type VendorCompletenessQuery = z.infer<typeof vendorCompletenessQuery>;

const optionalVendorText = z.string().trim().max(500).nullish();

/**
 * Inputs shared by live duplicate search and the server-side create recheck.
 *
 * `.strict()` per TRBUD-152, and this schema's failure mode is the inverse of the
 * others — it under-reports rather than over-reports, which is harder to notice.
 * A misspelled `?phoneNumber=` was dropped, so the search matched on name alone
 * and returned FEWER candidates. The screen then shows "no duplicates found" and
 * the user creates the vendor that already exists. An empty list reads as a clean
 * answer, so nothing prompts anyone to look again.
 *
 * The same shape as `v_vendor_match_queue` shipping with two of its four match
 * signals dead (CLAUDE.md): a silently narrowed duplicate check presents as
 * "nothing to review" rather than as a fault.
 */
export const vendorCandidatesQuery = z
  .object({
    name: z.string().trim().min(3).max(200),
    phone: z.string().trim().max(100).optional(),
    email: z.string().trim().email().max(320).optional(),
    website: z.string().trim().url().max(500).optional(),
  })
  .strict();
export type VendorCandidatesQuery = z.infer<typeof vendorCandidatesQuery>;

/** `GET /purchase-orders/supplier-options` — the purchasing picker's name search. */
export const purchaseOrderSupplierOptionsQuery = z
  .object({
    q: z.string().trim().max(200).optional(),
  })
  .strict();
export type PurchaseOrderSupplierOptionsQuery = z.infer<typeof purchaseOrderSupplierOptionsQuery>;

/**
 * A vendor's address, defined ONCE and spread into both the create body and the
 * retroactive-edit body (TRBUD-82).
 *
 * Two schemas rather than one shared object was the obvious shape and is the
 * wrong one: `POST /vendors` and `PATCH /vendors/:id/address` write the same
 * eight columns, and if they drift then a value accepted at creation is rejected
 * on edit — or worse, the reverse. Eden's requirement names both paths
 * explicitly ("upon new vendor entry and retroactively to already existing
 * profiles"), so keeping them literally the same object is the cheapest way to
 * keep that promise true.
 *
 * `region` is 100 rather than 500: it holds a state or province, and a 500-char
 * "region" is somebody pasting the whole address into the wrong box.
 * `postalCode` is 20 — long enough for any real format including spaces.
 * `countryCode` is a 2-letter ISO 3166-1 alpha-2 with NO default; see the column
 * comment for why `location` had to have its `US` default removed in 0064.
 */
const vendorAddressFields = {
  addressLine1: optionalVendorText,
  addressLine2: optionalVendorText,
  city: z.string().trim().max(200).nullish(),
  region: z.string().trim().max(100).nullish(),
  postalCode: z.string().trim().max(20).nullish(),
  countryCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, "expected a two-letter ISO 3166-1 country code, e.g. US or CA")
    .nullish(),
  /*
   * OPTIONAL coordinates. `coordinateString` is reused rather than re-derived —
   * it is the validator matched to `numeric(9,6)`, which is the column type here
   * too, and `coordinates.test.ts` exists because picking the nearest-looking
   * validator instead of the column's own is a defect this repo has shipped
   * three times.
   */
  latitude: coordinateString.nullish(),
  longitude: coordinateString.nullish(),
} as const;

/**
 * Both coordinates or neither, and each within its physical bound.
 *
 * ## Why a RANGE check lives here, when `coordinateString` deliberately omits one
 *
 * `coordinates.test.ts` states the rule: "The RANGE is deliberately not checked
 * here ... duplicating a bound in two places is how the two come to disagree."
 * That rule is about a *business* bound, which can be revised in one place and
 * not the other. ±90 and ±180 are not business bounds — they are the definition
 * of the coordinate system and cannot drift.
 *
 * The distinction is only worth anything if it is enforced, so
 * `vendor-address.dbtest.test.ts` asserts that this refinement and
 * `vendor_latitude_chk` / `vendor_longitude_chk` reject the *same* values. The
 * duplication is pinned by a test rather than by this paragraph.
 *
 * The reason it is worth duplicating at all: `coordinateString` admits three
 * integer digits, so `419` passes the shape check. Without this refinement a
 * human fat-fingering a latitude gets a raw `23514` naming a constraint, on a
 * form they are typing into by hand. Everything else on this body is hand-typed
 * too, which is exactly the situation the driver's GPS payload was not.
 */
export function checkVendorCoordinatePair(
  value: { latitude?: string | null; longitude?: string | null },
  ctx: z.RefinementCtx,
): void {
  const hasLatitude = value.latitude !== null && value.latitude !== undefined;
  const hasLongitude = value.longitude !== null && value.longitude !== undefined;

  if (hasLatitude !== hasLongitude) {
    ctx.addIssue({
      code: "custom",
      path: [hasLatitude ? "longitude" : "latitude"],
      message:
        "Give both a latitude and a longitude, or neither — a half pair cannot be mapped.",
    });
    // Fall through: the bound checks below still apply to whichever half is set,
    // so a user who typed one out-of-range value sees both problems at once
    // rather than fixing the pair and being refused a second time.
  }

  if (hasLatitude && Math.abs(Number.parseFloat(value.latitude as string)) > 90) {
    ctx.addIssue({
      code: "custom",
      path: ["latitude"],
      message: "Latitude must be between -90 and 90.",
    });
  }
  if (hasLongitude && Math.abs(Number.parseFloat(value.longitude as string)) > 180) {
    ctx.addIssue({
      code: "custom",
      path: ["longitude"],
      message: "Longitude must be between -180 and 180.",
    });
  }
}

/**
 * Set or clear a vendor's address — the retroactive path.
 *
 * Every field is nullish, and that is the point: Eden's requirement is that
 * somebody can fill an address in on any of the 85 vendors that already exist,
 * a few at a time. A body that demanded a complete address would make a
 * half-known one unsavable, and a half-known address still answers "is this
 * vendor near Clarksville".
 *
 * A field OMITTED is left alone; a field sent as `null` is cleared. That split
 * matters for a PATCH — without it, saving a coordinate would wipe the city.
 */
export const vendorAddressBody = strictObject({ ...vendorAddressFields, idempotencyKey })
  .superRefine(checkVendorCoordinatePair);
export type VendorAddressBody = z.infer<typeof vendorAddressBody>;

/**
 * The vendor's CONTACT record and its two roles — the other retroactive half.
 *
 * ## The hole this closes
 *
 * The whole vendor route surface was `GET /vendors`, `GET /vendors/candidates`,
 * `POST /vendors`, `PATCH /vendors/:id/status` and `PATCH /vendors/:id/address`.
 * So notes, phone, email, website and the two role flags were settable exactly
 * once, at creation, and **could not be edited afterwards by anybody** — a typo
 * in a phone number was permanent, and Rush Truck Centers turning out to sell
 * parts as well as fix trucks needed a second vendor record, which is a split
 * spend history and the very defect `is_parts_supplier` exists to prevent.
 *
 * `vendor.notes` had a single writer in the entire repo: the INSERT in
 * `POST /vendors`. `tb_app` has held `UPDATE (notes)` the whole time — the
 * privilege was never the blocker, only the route. That is the same shape
 * TRBUD-249 recorded for `work_order.custom_fields`.
 *
 * ## Same PATCH semantics as the address body
 *
 * A field OMITTED is left alone; a field sent as `null` is cleared. Deliberately
 * NOT merged into `vendorAddressBody`: the two bodies write disjoint column sets
 * under one capability, and one combined body would make "clear the address"
 * and "correct the phone" the same request, so a client that sent only a phone
 * would have to re-send eight address fields it never looked at.
 *
 * ## The roles are `optional()`, not `nullish()`
 *
 * `is_repair_vendor`, `is_parts_supplier` and `is_external_service` are
 * `NOT NULL` columns, so `null` is not a value they can take and offering it
 * would be a promise the database refuses. `is_mobile_service` IS nullable, so
 * it takes the full three-way `nullish()` — omitted, set, or explicitly cleared
 * back to "nobody has said". That asymmetry is the column definitions showing
 * through, and flattening it would either invent a default or lose the ability
 * to un-answer the question.
 *
 * ## THREE roles since TRBUD-263
 *
 * `isExternalService` joins the two rather than replacing them — Eden,
 * 15 September 2026: "add external services as a third tick-box". Note that the
 * body does NOT refine on "at least one is true" the way `createVendorBody`
 * does, and that is not an oversight: a PATCH omits what it does not mention,
 * so this schema cannot see the flags it was not sent and any refusal decided
 * here would be judging a partial body. The route merges the incoming flags
 * over the stored row and refuses there, which is the only place the whole
 * triple is known.
 */
export const vendorProfileBody = strictObject({
  phone: phoneString,
  email: z.string().trim().email().max(320).nullish(),
  website: z.string().trim().url().max(500).nullish(),
  /*
   * `optionalVendorText`, the SAME 500-char bound `createVendorBody` uses,
   * rather than a wider one chosen for this body alone. The rule is the one
   * written out on `vendorAddressFields` above: two schemas writing the same
   * column must not drift, "or a value accepted at creation is rejected on edit
   * — or worse, the reverse". The longest note in the 15 September import is 58
   * characters, so there is nothing to buy by widening it.
   */
  notes: optionalVendorText,
  isRepairVendor: z.boolean().optional(),
  isPartsSupplier: z.boolean().optional(),
  isExternalService: z.boolean().optional(),
  isMobileService: z.boolean().nullish(),
  hasAccount: z.boolean().nullish(),
  accountReference: z.string().trim().max(200).nullish(),
  /*
   * TRBUD-262. `nullish` rather than `optional`, matching `isMobileService`
   * directly above and for the same reason: the column is nullable because NULL
   * means nobody has been asked, and an editor has to be able to put it BACK to
   * unknown. `optional` alone would make "not a chain" and "never answered" the
   * same edit, which is the distinction the column exists to keep.
   */
  isChain: z.boolean().nullish(),
  idempotencyKey,
});
export type VendorProfileBody = z.infer<typeof vendorProfileBody>;

export const createVendorBody = strictObject({
  name: z.string().trim().min(1).max(200),
  phone: phoneString,
  email: z.string().trim().email().max(320).nullish(),
  website: z.string().trim().url().max(500).nullish(),
  notes: optionalVendorText,
  ...vendorAddressFields,
  isRepairVendor: z.boolean().default(true),
  isPartsSupplier: z.boolean().default(false),
  /*
   * The third role (TRBUD-263). `false` by default, and unlike the two above it
   * that default is not a claim about anything: a create body that says nothing
   * about external services is a vendor nobody called an external service.
   *
   * Do NOT confuse this with `externalShareEnabled` directly below, which is a
   * SHARING switch — whether an outside party may be given a link — and has
   * nothing to do with what the vendor sells. The names are close enough that
   * the pair is worth reading twice.
   */
  isExternalService: z.boolean().default(false),
  /*
   * One branch of a national chain (TRBUD-262). NOT a fourth role — it takes no
   * part in the three-arm refine below, because "is this one of many under a
   * shared name" is orthogonal to what the vendor does. A Goodyear branch is a
   * repair vendor AND a chain.
   *
   * `default(false)` here while the COLUMN is nullable, and the difference is
   * deliberate rather than sloppy. NULL on the column means nobody has been
   * asked, which is true of all 85 landed vendors. A create body comes from a
   * person who was shown the tick-box, so an unticked box is an answer — "no" —
   * not an absence. The profile body above keeps `nullish` so an editor can
   * return a row to unknown; only creation can honestly default.
   */
  isChain: z.boolean().default(false),
  hasAccount: z.boolean().nullish(),
  accountReference: z.string().trim().max(200).nullish(),
  externalShareEnabled: z.boolean().default(false),
  acknowledgedCandidateIds: z.array(z.string().uuid()).max(100).default([]),
  idempotencyKey,
})
  /*
   * `vendor_role_chk`'s three-arm OR, mirrored so a create that the database
   * would refuse gets a sentence instead of a 23514 naming a constraint.
   *
   * NO `path`. It used to name `isRepairVendor`, which was defensible with two
   * flags and is misleading with three — somebody who unticked "External
   * services" would be handed a complaint about the repair field. `parseBody`
   * prints the bare message when the path is empty, so dropping it makes the
   * refusal read as the form-level rule it actually is.
   */
  .refine(
    (value) => value.isRepairVendor || value.isPartsSupplier || value.isExternalService,
    { message: "A vendor must be a repair vendor, a parts supplier, an external service, or any combination." },
  )
  /*
   * The SAME coordinate rules as `vendorAddressBody`, via the same function
   * rather than a second copy of the three checks. A repeated rule is a rule
   * that gets fixed in one place: a vendor created with a latitude and no
   * longitude would otherwise reach `vendor_coordinates_shape_chk` and be
   * refused by Postgres with a constraint name instead of a sentence.
   */
  .superRefine(checkVendorCoordinatePair);
export type CreateVendorBody = z.infer<typeof createVendorBody>;

export const vendorMatchDecisionBody = strictObject({
  vendorAId: z.string().uuid(),
  vendorBId: z.string().uuid(),
  verdict: z.enum(["distinct", "duplicate"]),
  cause: z.string().trim().min(1).max(1000),
  idempotencyKey,
}).refine((value) => value.vendorAId !== value.vendorBId, {
  message: "A decision requires two different vendors.",
  path: ["vendorBId"],
});
export type VendorMatchDecisionBody = z.infer<typeof vendorMatchDecisionBody>;

/**
 * Set a vendor's status.
 *
 * `reason` is required for the two that REFUSE a vendor and optional otherwise.
 * Blacklisting a supplier is a decision somebody will be asked about months
 * later, and "who set this and why" is the whole value of recording it; the
 * audit envelope carries the who, this carries the why.
 */
export const setVendorStatusBody = strictObject({
    status: z.enum(vendorStatuses),
    reason: z.string().trim().max(500).optional(),
    idempotencyKey,
  })
  .superRefine((value, ctx) => {
    if ((value.status === "blacklisted" || value.status === "inactive") && !value.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: "Taking a vendor out of use needs a reason.",
      });
    }
  });
export type SetVendorStatusBody = z.infer<typeof setVendorStatusBody>;

/**
 * Retire a vendor contact: `active = false`, which takes the person out of the
 * assign picker. It is also the undo for an imported contact already assigned
 * to a work order, whose assignment row cannot lose its only subject
 * (record-details plan, sections 6.2 and 8).
 */
export const retireVendorContactBody = strictObject({ idempotencyKey });
export type RetireVendorContactBody = z.infer<typeof retireVendorContactBody>;

/*
 * ── Adding and editing a vendor's contacts (TRBUD-466) ──────────────
 *
 * The two writes behind "Add contact" and "Edit". Strict, so a body naming the
 * provenance columns (`sourceSystem`, `sourceId`) is refused rather than
 * dropped: only the MaintainX import may say a contact came from MaintainX, and
 * `tb_app`'s column grants on `vendor_contact` leave those two out for the same
 * reason. Lengths are the vendor record's own: a name or title is `TEXT_MAX.name`,
 * an email `TEXT_MAX.email`, a phone `phoneString`'s shape (a note is refused).
 */
const vendorContactName = z.string().trim().min(1, "Give the contact a name.").max(TEXT_MAX.name);
const vendorContactTitle = z.string().trim().max(TEXT_MAX.name).nullish();
const vendorContactEmail = z.string().trim().email().max(TEXT_MAX.email).nullish();

/**
 * `POST /vendors/:id/contacts` — `manageVendors`. `isPrimary` moves the primary
 * mark to this contact from whoever held it.
 */
export const createVendorContactBody = strictObject({
  name: vendorContactName,
  title: vendorContactTitle,
  phone: phoneString,
  email: vendorContactEmail,
  isPrimary: z.boolean().default(false),
  idempotencyKey,
});
export type CreateVendorContactBody = z.infer<typeof createVendorContactBody>;

/**
 * `PATCH /vendors/:id/contacts/:contactId` — `manageVendors`. The vendor
 * profile's PATCH rules: a field OMITTED is left alone, `null` clears it.
 * `isPrimary: true` moves the primary mark here; `false` takes it off. A retired
 * contact is refused (422): a different person is retire plus add, because a
 * rename changes the name on every past work order that assigned this contact.
 */
export const editVendorContactBody = strictObject({
  name: vendorContactName.optional(),
  title: vendorContactTitle,
  phone: phoneString,
  email: vendorContactEmail,
  isPrimary: z.boolean().optional(),
  idempotencyKey,
});
export type EditVendorContactBody = z.infer<typeof editVendorContactBody>;

/**
 * The five values `location_kind_chk` allows.
 *
 * Kept module-private rather than promoted into `vocabulary.ts`, because nothing
 * else reads it — there is no label map, no filter and no picker keyed on it
 * today. The CHECK in `schema/core/vendor.ts` is the authority; this mirrors it
 * so a caller gets a list of the five instead of a `23514` naming a constraint.
 */
/**
 * `location_kind_chk`. Exported because the create form renders these options,
 * and a second hand-written copy in `apps/web` would drift from the CHECK the
 * moment either changed.
 */
export const locationKinds = ["yard", "shop", "vendor", "mobile", "other"] as const;
export type LocationKind = (typeof locationKinds)[number];

/**
 * Create a stocking location — the cold-start hole underneath purchasing.
 *
 * ## Why this body exists at all (F-06 / TRBUD-113)
 *
 * `location` had **no writer reachable from the product**: only `seed-demo.ts`
 * and the MaintainX projector wrote it. On a fresh environment the table is
 * therefore empty and stays empty — and `purchase_order.deliver_to_location_id`
 * is NOT NULL, so every purchase order was refused `23503`, as was every
 * movement in the parts ledger (`inventory_txn.location_id` is NOT NULL too).
 * A gate whose only key is the activity it refuses is the softlock CLAUDE.md's
 * cold-start rule forbids.
 *
 * `kind` DEFAULTS to the column's own default rather than to a guess at what a
 * first location usually is. The API and the column agreeing means a body that
 * omits the field lands the same row a direct INSERT would.
 *
 * The address fields are `vendorAddressFields`, reused rather than restated:
 * `location` carries the same eight columns as `vendor`, with the same types and
 * the same `numeric(9,6)` coordinates. A second copy is a second place for the
 * coordinate rule to drift.
 */
/**
 * Record a vendor's invoice against costs already posted to a work order — M0.
 *
 * `documentId` is REQUIRED because `vendor_invoice.document_id` is NOT NULL: an
 * invoice cannot be recorded without the file it came from. That is a deliberate
 * property of the schema, not an oversight to route around.
 *
 * `costEntryIds` is non-empty for the reason the whole wave exists. The
 * `builtin:final_invoice` gate reads `vendor_invoice_cost_entry`, the JOIN
 * table — so an invoice with nothing attached satisfies nothing, and accepting
 * an empty list would let a caller believe they had closed the job.
 */
export const createVendorInvoiceBody = strictObject({
    purchasePayment: purchasePaymentBody.optional(),
    vendorId: z.string().uuid(),
    invoiceNumber: z.string().trim().min(1).max(100),
    currency: currencyCode,
    totalAmount: positiveDecimalString,
    invoiceDate: boundedDateString("recorded"),
    dueDate: boundedDateString("scheduled").nullish(),
    documentId: z.string().uuid(),
    /** Bigint ids, carried as strings — `cost_entry.id` exceeds Number.MAX_SAFE_INTEGER's guarantee. */
    costEntryIds: z.array(bigintIdString("expected a cost entry id")).min(1).max(100),
    idempotencyKey,
  })
  .strict();
export type CreateVendorInvoiceBody = z.infer<typeof createVendorInvoiceBody>;

/**
 * The legal statuses, matching `vendor_invoice_current_status_chk`.
 *
 * `received` is absent on purpose: it is the state the row is created in, so
 * "move it to received" is not a transition anybody needs to ask for.
 */
export const invoiceStatuses = ["approved", "disputed", "partially_paid", "paid", "void"] as const;

export const setInvoiceStatusBody = strictObject({
    status: z.enum(invoiceStatuses),
    reason: z.string().trim().max(1000).nullish(),
    idempotencyKey,
  })
  .strict();
export type SetInvoiceStatusBody = z.infer<typeof setInvoiceStatusBody>;

/**
 * The most files one receipt may run over, page 1 included — TRBUD-352. Here
 * rather than in `receipt-continuation.ts` because this module builds its
 * schemas at load and that one imports from it, never the other way.
 */
export const MAX_RECEIPT_PAGES = 20;

/**
 * One line of an invoice a PERSON is reading and typing — TRBUD-162.
 *
 * ## What is required, and what is deliberately absent
 *
 * `description` and `amount` are REQUIRED. `proposed_cost_line.amount` is
 * nullable at the database level, which is right for a partial AI extraction —
 * a parser that could not read a figure should say so rather than invent one —
 * and wrong for a person with the paper in front of them. Eden: the person
 * *"enters the receipt or invoice as an ITEMISED LIST, so the business has a
 * record of its spending."* A line with no amount is not a record of spending.
 *
 * **There is NO `confidence` field, and `.strict()` makes sending one a 400.**
 * Eden, 3 September 2026: *"I dont want them to have to waste time entering a
 * confident % since thats for the AI."* The column stays NULL for every
 * hand-entered line, which is precisely what it is nullable for: a person's line
 * says "no score" rather than inventing 1.0, and only a parser ever writes it.
 * The refusal lives in the schema rather than in the handler so that no future
 * client can smuggle one in.
 *
 * `amount` is a POSITIVE magnitude for every kind, `DISCOUNT` included. The
 * server applies the sign from the kind, because `cost_entry_sign_chk` derives
 * `accounting_category` from the sign and a typed minus sign is how a credit ends
 * up posted as a charge. See `invoiceLineKinds`.
 *
 * It may be ZERO. A dealer invoice prints no-charge lines ("R&R CCV HOUSING",
 * 0.00), and refusing one made the person delete a line the paper carries,
 * which the parser feedback then reads as the AI being wrong (Eden, 1 October
 * 2026). The ledger still holds no zero entry (`cost_entry_amount_nonzero_chk`),
 * so confirming accepts a zero line and posts nothing for it.
 *
 * `quantity` and `unitCost` take TWO decimal places and no sign
 * (`invoiceQuantityString` / `invoiceUnitCostString`), since 2 October 2026.
 * They used `quantityString` / `unitCostString`, matched to
 * `proposed_cost_line`'s `numeric(12,3)` and `numeric(10,4)`; Eden ruled that an
 * invoice line needs two places for all three figures, and the entry forms
 * round the receipt reader's wider figures to two before they are sent
 * (`invoiceLineFigure`). The columns keep their scales and a two-place value
 * fits both, so this narrows what a person may TYPE, not what the table holds.
 * The sign is refused with its own sentence rather than as a shape fault: it is
 * the kind's to give.
 */
export const manualInvoiceLineInput = z
  .object({
    /** Reviewed work/occurrence day; null explicitly records that it is unknown. */
    serviceDate: boundedDateString("recorded").nullish(),
    description: z.string().trim().min(1).max(500),
    kind: z.enum(invoiceLineKinds),
    amount: magnitudeDecimalString,
    quantity: invoiceQuantityString.nullish(),
    unitCost: invoiceUnitCostString.nullish(),
    /** Only explicitly labelled vendor hours count as hours; lump sums are flat rate. */
    labourUnit: z.enum(["hours", "flat_rate"]).optional(),
    /** Set when the line is a catalogue part, so the PO comparison can match on it. */
    partId: z.string().uuid().nullish(),
    /**
     * The AI proposal's line this one was pre-filled from (its `lineNumber`),
     * when the form started from a proposal. Absent for a line the person added.
     * It is what lets the server log accept / correct / reject per AI line.
     */
    sourceLine: z.number().int().min(1).max(1000).nullish(),
    /**
     * Which page of a receipt of several files `sourceLine` is on (TRBUD-352).
     * Absent means page 1, which is every one-file receipt.
     */
    sourcePage: z.number().int().min(1).max(MAX_RECEIPT_PAGES).nullish(),
  })
  .strict()
  .superRefine((line, ctx) => {
    if (line.labourUnit !== undefined && line.kind !== "LABOR")
      ctx.addIssue({ code: "custom", path: ["labourUnit"], message: "A labor unit belongs on a vendor labor line." });
    if (line.labourUnit === "hours" && (line.quantity == null || Number(line.quantity) <= 0))
      ctx.addIssue({ code: "custom", path: ["quantity"], message: "Enter the hours listed on the invoice." });
  });
export type ManualInvoiceLineInput = z.infer<typeof manualInvoiceLineInput>;

/**
 * Log a vendor's invoice by hand, itemised, against a work order — TRBUD-162.
 *
 * ## One request, because it is one act
 *
 * The header and the lines arrive together and are written in one audited
 * transaction. An invoice with no lines satisfies nothing and is not a record of
 * spending — and a two-step flow leaves a dangling `vendor_invoice` that
 * `vendor_invoice_vendor_number_uq` then makes UNRECOVERABLE: the second attempt
 * at the same invoice number is refused by the very constraint that exists to
 * prevent double-logging. That is the difference between an atomic create and a
 * merely convenient one.
 *
 * ## Why `documentId` is required
 *
 * `vendor_invoice.document_id` is NOT NULL: an invoice cannot be recorded without
 * the file it came from. Document intake is a hard prerequisite for invoicing
 * rather than an adjacent feature, which is why TRBUD-162 carries both halves.
 *
 * ## `purchaseOrderId` is optional, and its absence must not block anything
 *
 * Supplied, the server compares each line against `purchase_order_line` — was
 * this ordered, at that quantity, at that price — and writes what it finds to
 * `proposed_cost_line.variance_reason`. Absent, nothing is compared and nothing
 * is refused. Requiring it would be the cold-start mistake in miniature: most
 * jobs have no purchase order, and a check whose only satisfier is a document
 * nobody raised is a check that stops work.
 */
export const createManualInvoiceBody = strictObject({
    /** Default for lines without their own service day. Never the billing date. */
    serviceDate: boundedDateString("recorded").nullish(),
    purchasePayment: purchasePaymentBody.optional(),
    documentId: z.string().uuid(),
    vendorId: z.string().uuid(),
    invoiceNumber: z.string().trim().min(1).max(100),
    invoiceDate: boundedDateString("recorded"),
    dueDate: boundedDateString("scheduled").nullish(),
    currency: currencyCode,
    totalAmount: positiveDecimalString,
    foreignReceipt: strictObject({ currency: z.enum(receiptReferenceCurrencies), amount: positiveDecimalString }).optional(),
    purchaseOrderId: z.string().uuid().nullish(),
    /**
     * TRBUD-412: the shop that did the work, when another company bills for it.
     * Absent is no other shop. The biller (`vendorId`) keeps the spend; the shop
     * decides only whether it reads as vendor or in-house spend.
     */
    performedBy: performedByInput.optional(),
    /**
     * At least one. An invoice with nothing on it is not a logged invoice, and
     * accepting an empty list would let somebody believe they had recorded their
     * spending. The ceiling matches `createVendorInvoiceBody.costEntryIds`.
     */
    lines: z.array(manualInvoiceLineInput).min(1).max(100),
    /**
     * The parser extraction the form was pre-filled from, when there was one
     * (`AiProposal.extractionId`). With it the server writes `parser_feedback`
     * for every AI line in the same transaction as the invoice. Without it
     * nothing about a parser is recorded, which is the ordinary manual path.
     */
    aiExtractionId: z.string().uuid().nullish(),
    /**
     * TRBUD-352: the documents of pages 2..n of a receipt of several files, in
     * the order the screen showed them. The server compares them with the pages
     * joined now and answers 409 when they differ, so a page joined while the
     * receipt was being typed is never swept into a bill nobody typed it for.
     * Absent means a one-file receipt.
     */
    pageDocumentIds: z.array(z.string().uuid()).max(MAX_RECEIPT_PAGES - 1).optional(),
    /**
     * TRBUD-352: on a receipt of several files, the pages the form was pre-filled
     * from, each with its own AI extraction, in place of `aiExtractionId`. The
     * route refuses both together and a page named twice
     * (`manualInvoiceAiPagesRefusal`).
     */
    aiPages: z
      .array(
        strictObject({
          page: z.number().int().min(1).max(MAX_RECEIPT_PAGES),
          extractionId: z.string().uuid(),
        }),
      )
      .min(1)
      .max(MAX_RECEIPT_PAGES)
      .nullish(),
    idempotencyKey,
  })
  .strict();
export type CreateManualInvoiceBody = z.infer<typeof createManualInvoiceBody>;

/**
 * Log a vendor's bill against a PURCHASE ORDER — docs/plans/trbud-po-bills.md.
 *
 * Eden, 6 October 2026, on the receipts the MaintainX import attached to its
 * purchase orders: *"I need these to be actual valid entries to be logged in the
 * database"*. And, the same day: *"they could be directly associated with a
 * work order OR they could just be an inventory restock with no WO
 * associated."* So `workOrderId` is optional and decides what the bill is:
 *
 * - **absent: a restock.** A bill on the order, with no job cost — the parts
 *   reach a job's cost when they are issued, so posting them here as well would
 *   count the same money twice.
 * - **present: bought for that job.** The receipt is attached to the job and
 *   the bill is that job's invoice, compared with the order; confirming posts
 *   each line to the job, as any receipt on a work order does.
 *
 * The work-order body without `purchaseOrderId`: the order is in the URL.
 *
 * ## `aiExtractionId` and `aiPages` are KEPT (9 October 2026)
 *
 * They were omitted while the only files on an order were the MaintainX PDFs,
 * which no receipt parser reads. A receipt UPLOADED to an order is read like
 * any other (TRBUD-351), and Eden ruled that a PO receipt is entered like a
 * work-order receipt: *"so the same AI connection and settings are applied to
 * it"*. So the form is pre-filled from the AI's read, and the save names the
 * read it started from, exactly as the work-order body does; the route writes
 * the same `parser_feedback` for it (`recordSaveFeedback`). Neither, as on the
 * work-order route, is the ordinary hand-typed bill. The "not both" rule is
 * `manualInvoiceAiPagesRefusal`, asked by both routes.
 */
export const createPurchaseOrderBillBody = createManualInvoiceBody
  .omit({ purchaseOrderId: true })
  .extend({ workOrderId: z.string().uuid().nullish() })
  .strict();
export type CreatePurchaseOrderBillBody = z.infer<typeof createPurchaseOrderBillBody>;

/**
 * Put a photo that is on a purchase order onto one of that order's parts
 * (`POST /purchase-orders/:id/part-photos`, TRBUD-351). Eden, 8 October 2026:
 * *"if the purchase order has like a parts image associated with it I want it
 * added as the part image in the parts page for it."*
 *
 * The order is in the URL. The server checks the file is on the order, is an
 * image the page can show and not a receipt, and that the part is on a live
 * line of the order.
 */
export const addPurchaseOrderPartPhotoBody = strictObject({
  documentId: z.string().uuid(),
  partId: z.string().uuid(),
  idempotencyKey,
});
export type AddPurchaseOrderPartPhotoBody = z.infer<typeof addPurchaseOrderPartPhotoBody>;

export const createLocationBody = strictObject({
    name: z.string().trim().min(1).max(200),
    kind: z.enum(locationKinds).default("other"),
    vendorId: z.string().uuid().nullish(),
    ...vendorAddressFields,
    idempotencyKey,
  })
  .superRefine(checkVendorCoordinatePair)
  .superRefine((value, ctx) => {
    // `location_vendor_kind_chk` is `vendor_id IS NULL OR kind = 'vendor'`.
    // Refusing here rather than at the database turns a constraint name into a
    // sentence naming the field the caller has to change.
    if (value.vendorId && value.kind !== "vendor") {
      ctx.addIssue({
        code: "custom",
        path: ["kind"],
        message: "Only a vendor location may name a vendor. Set the kind to 'vendor', or drop the vendor.",
      });
    }
  });
export type CreateLocationBody = z.infer<typeof createLocationBody>;

/**
 * Create a catalogue part.
 *
 * ## `unitOfMeasure` is REQUIRED here even though the column is nullable
 *
 * `part.unit_of_measure` was deliberately relaxed to nullable on 17 August 2026,
 * because NULL is a real state for a projected part — MaintainX supplies no unit
 * for any of its 65, and inventing one would enforce the invention on every
 * later movement. That reasoning is about the IMPORTER, and it does not carry
 * over to a human typing a form: `record_inventory_txn` refuses to move stock
 * for a part whose unit is unresolved, and **nothing in the product can set it
 * afterwards** — `tb_app` holds UPDATE on the column but no screen or route
 * writes it. A part created without a unit would therefore be permanently
 * unmovable, which is a fresh cold-start dead end rather than a tolerated
 * absence. So the field is required on this path and nullable on the other.
 *
 * ## `unitCost` is OPTIONAL, and the route warns instead of refusing
 *
 * The mirror-image decision, and the cold-start rule's own words: absence warns,
 * allows, and is RECORDED. A price the business genuinely does not have yet is
 * ordinary — 7 of the 65 MaintainX parts carry none — so refusing would be a
 * second gate keyed on a missing signal. It is accepted **at creation only**:
 * `02-grants.sql` REVOKEs UPDATE on `unit_cost` from `tb_app` on purpose, so a
 * later change is not available to this tier and the value cannot be rewritten
 * untracked. The insert runs inside `withAudit`, so who set it and when is in
 * `audit_log`.
 *
 * `unitCostString` and not `decimalString`: `part.unit_cost` is
 * `numeric(10,4)`, and `decimalString` is a REGEX matched to `numeric(10,2)`, so
 * it would REJECT `12.5000` outright rather than round it. See its own comment —
 * this repo has shipped that defect three times.
 */
export const createPartBody = strictObject({
  partNumber: z.string().trim().min(1).max(100),
  /**
   * The human label, not a blurb. `part` has no `name` column, so this IS the
   * label the parts picker renders — see the column's comment for why MaintainX's
   * multi-line `description` deliberately does not land here.
   */
  description: z.string().trim().min(1).max(500),
  unitOfMeasure: z.string().trim().min(1).max(50),
  manufacturer: z.string().trim().max(200).nullish(),
  unitCost: unitCostString.nullish(),
  isStocked: z.boolean().default(false),
  defaultSupplierVendorId: z.string().uuid().nullish(),
  idempotencyKey,
});
export type CreatePartBody = z.infer<typeof createPartBody>;

/**
 * One line on a purchase order.
 *
 * `extendedCost` is NOT sent by the client — the server computes it from
 * quantity x unit cost. `purchase_order_line_extended_chk` has no variance
 * escape (unlike `cost_entry`, whose vendor may round differently), so a client
 * that computed it slightly differently would be refused by the database rather
 * than accepted with a wrong number. Deriving it server-side removes the
 * question entirely.
 */
export const purchaseOrderLineBody = strictObject({
    partId: z.string().uuid().nullish(),
    freeTextDescription: z.string().trim().min(1).max(500).nullish(),
    quantity: quantityString,
    unitOfMeasure: z.string().trim().min(1).max(50),
    unitCost: unitCostString,
    taxAmount: decimalString.nullish(),
  })
  .superRefine((value, ctx) => {
    // Exactly one of the two, mirroring `purchase_order_line_subject_chk`. A
    // one-off buy that is not a catalogue part is legitimate and must not force
    // a catalogue entry; a line that is neither names nothing at all.
    const named = [value.partId, value.freeTextDescription].filter(
      (v) => v !== null && v !== undefined && v !== "",
    ).length;
    if (named !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["partId"],
        message: "A line names either a catalogue part or a free-text description, not both.",
      });
    }
  });
export type PurchaseOrderLineBody = z.infer<typeof purchaseOrderLineBody>;

/**
 * Save a purchase-order draft.
 *
 * The whole draft every time, rather than a patch. A PO is a document being
 * authored: the lines are the intended final set, so a repeat of the same
 * request is a no-op by construction — the same reasoning that makes
 * `PUT /work-orders/:id/categories` a PUT.
 */
export const savePurchaseOrderBody = strictObject({
    vendorId: z.string().uuid(),
    purpose: z.enum(["replenishment", "work_order_specific"]),
    workOrderId: z.string().uuid().nullish(),
    assetId: z.string().uuid().nullish(),
    deliverToLocationId: z.string().uuid(),
    currency: currencyCodeDefaulted,
    neededBy: boundedDateString("scheduled").nullish(),
    terms: z.string().trim().max(200).nullish(),
    notes: z.string().trim().max(4000).nullish(),
    creditRequested: z.boolean().default(false),
    shippingAmount: decimalString.nullish(),
    taxAmount: decimalString.nullish(),
    lines: z.array(purchaseOrderLineBody).max(200),
    idempotencyKey,
  })
  .superRefine((value, ctx) => {
    // Requirement 11.17, refused here as well as by the two CHECKs, so the
    // caller gets a sentence rather than a constraint name.
    if ((value.purpose === "work_order_specific") !== !!value.workOrderId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["workOrderId"],
        message: "A work-order purchase order names a work order; a replenishment one does not.",
      });
    }
    if (value.workOrderId && !value.assetId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assetId"],
        message: "A purchase order links to a work order AND its asset, or to neither (11.17).",
      });
    }
  });
export type SavePurchaseOrderBody = z.infer<typeof savePurchaseOrderBody>;

/**
 * Move a draft on.
 *
 * `submitted` means SAVED AND MARKED READY. There is no approval gate — ruling
 * 1.7 makes approval a permission switch that does not exist yet — so this must
 * never be labelled or described as approval.
 */
export const purchaseOrderStatusBody = strictObject({
    status: z.enum(["draft", "submitted", "approved", "rejected", "cancelled"]),
    cancelReason: z.string().trim().max(500).optional(),
    rejectReason: z.string().trim().max(500).optional(),
    idempotencyKey,
  })
  .superRefine((value, ctx) => {
    if (value.status === "cancelled" && !value.cancelReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["cancelReason"],
        message: "Cancelling a purchase order needs a reason.",
      });
    }
    if (value.status === "rejected" && !value.rejectReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rejectReason"],
        message: "Rejecting a purchase order needs a reason.",
      });
    }
  });
export type PurchaseOrderStatusBody = z.infer<typeof purchaseOrderStatusBody>;

/**
 * Turn the second-approver requirement on purchase orders on or off (F-41).
 *
 * A bare boolean, and deliberately not a threshold or a role list: the rule is
 * "the person who raised it may not approve it", and who MAY approve is already
 * `routine_permission.approve_purchase_order`, editable on the permissions
 * screen. Two places to express one policy would be two places to disagree.
 *
 * No `idempotencyKey`: the request states the intended final value, so replaying
 * it sets the same thing again. `set_purchasing_policy` is an upsert.
 */
export const setPurchasingPolicyBody = strictObject({
  requireSecondApprover: z.boolean(),
});
export type SetPurchasingPolicyBody = z.infer<typeof setPurchasingPolicyBody>;

export const recordPurchaseReceiptLineBody = strictObject({
    purchaseOrderLineId: z.string().uuid(),
    quantityReceived: quantityString,
    quantityRejected: decimalString.optional(),
    rejectReason: z.string().trim().max(500).optional(),
  })
  .superRefine((value, ctx) => {
    if (Number(value.quantityRejected ?? "0") > 0 && !value.rejectReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rejectReason"],
        message: "A rejected quantity needs a reason.",
      });
    }
  });

export const recordPurchaseReceiptBody = strictObject({
  locationId: z.string().uuid(),
  lines: z.array(recordPurchaseReceiptLineBody).min(1).max(200),
  note: z.string().trim().max(2000).optional(),
  idempotencyKey,
});
export type RecordPurchaseReceiptBody = z.infer<typeof recordPurchaseReceiptBody>;

/**
 * The parts catalogue's filters.
 *
 * `.strict()` per TRBUD-152. `?belowMinimum=true` or `?lowStock=true` — the two
 * things a caller reaches for instead of `flaggedOnly` — were dropped, and the
 * response was the whole catalogue with a 200. On a reorder screen that is the
 * expensive direction of the defect: "nothing is below minimum" and "every part
 * is listed" arrive looking the same, and the second is what you get.
 */
export const partsQuery = z
  .object({
    q: z.string().trim().max(200).optional(),
    location: z.string().trim().max(200).optional(),
    flaggedOnly: queryBoolean.optional(),
  })
  .strict();
export type PartsQuery = z.infer<typeof partsQuery>;

/**
 * The insights screen's one control.
 *
 * `.strict()` per TRBUD-152, and the `.default("30d")` is exactly what made this
 * one dangerous. A misspelled `?period=ytd` was dropped, the default then filled
 * `timeframe` in, and the response was a complete, plausible, well-formed set of
 * 30-day figures presented as the year to date. Every other schema in this set
 * at worst returns too many rows; this one returns the WRONG NUMBER, and a
 * default is what makes the wrong number look deliberate.
 */
export const reportsQuery = z
  .object({
    timeframe: z.enum(["all", "30d", "quarter", "ytd"]).default("all"),
    jobCategoryId: z.union([z.uuid(), z.literal("uncategorized")]).optional(),
    vendorId: z.uuid().optional(),
    start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(isRealCalendarDay).optional(),
    end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(isRealCalendarDay).optional(),
  })
  .strict()
  .superRefine((value, context) => {
    // Lifetime detail/export may cap its unbounded history at the summary
    // response's end day. Finite/custom ranges still require both dates.
    if (Boolean(value.start) !== Boolean(value.end) && !(value.timeframe === "all" && !value.start && value.end)) {
      context.addIssue({ code: "custom", message: "Provide both start and end dates for a custom range." });
    } else if (value.start && value.end && value.start > value.end) {
      context.addIssue({ code: "custom", message: "Start date must be on or before end date." });
    }
  });
export type ReportsQuery = z.infer<typeof reportsQuery>;

/** Dated category reconciliation and paginated supporting cost lines. */
export const categoryPeriodQuery = reportsQuery.safeExtend({
  categoryId: z.union([z.uuid(), z.literal("uncategorized")]).optional(),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

/** Exact supporting population for a displayed monthly spend segment. Read-only
 * historical currencies remain queryable without expanding currency write rules. */
export const spendSegmentQuery = reportsQuery.safeExtend({
  // TRBUD-401: optional, and absent means the whole window (a spend card cell).
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).refine((value) => !value.startsWith("0000-"), "Choose a valid calendar month.").optional(),
  // TRBUD-401: `all` is the chart's Total column, every side together.
  side: z.enum(["vendor", "in_house", "maintainx_unsplit", "all"]),
  spendClass: z.enum(["labour", "parts", "other"]).optional(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

/** Opened/completed work-type events and their supporting work orders. */
/* ── TRBUD-401: report drill-through ─────────────────────────────── */

/** One page of report drill rows, or the whole set for an export. */
const drillPage = {
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
};

/** The intervals behind one Downtime by unit bar, in the chart's window and filters. */
export const downtimeUnitQuery = reportsQuery.safeExtend({
  assetId: z.uuid(),
  ...drillPage,
});
export type DowntimeUnitQuery = z.infer<typeof downtimeUnitQuery>;

/**
 * The labor behind one row of the Labor ROI figures. `currency` is the card's
 * currency; with `includeUnpriced` the MaintainX hours imported at no rate come
 * too, and with no currency they come alone (the card's "MaintainX, no rate").
 */
export const labourLinesQuery = reportsQuery.safeExtend({
  timeKind: z.enum(["repair_labour", "admin_processing"]).optional(),
  currency: z.string().regex(/^[A-Z]{3}$/).optional(),
  includeUnpriced: queryBoolean.default(false),
  ...drillPage,
});
export type LabourLinesQuery = z.infer<typeof labourLinesQuery>;

/** The work orders behind the Created vs completed tile, by the card's own rule. */
export const activityWorkOrdersQuery = reportsQuery.safeExtend({
  cohort: z.enum(reportActivityCohorts),
  ...drillPage,
});
export type ActivityWorkOrdersQuery = z.infer<typeof activityWorkOrdersQuery>;

/** The jobs behind one cash group, by the cash forecast's own classification. */
export const cashForecastJobsQuery = reportsQuery.safeExtend({
  cohort: z.enum(cashForecastCohortKeys),
  ...drillPage,
});
export type CashForecastJobsQuery = z.infer<typeof cashForecastJobsQuery>;

export const reportActivityQuery = reportsQuery.safeExtend({
  // TRBUD-448: In-house or Vendor only, by the Work orders page's own rule
  // (TRBUD-357). Absent means every job. The values are the queue's.
  repairKind: z.enum(reportRepairKinds).optional(),
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
  typeId: z.uuid().optional(),
  activity: z.enum(["opened", "completed"]).optional(),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

/**
 * "This route takes no query parameters" — said out loud, so it can be enforced.
 *
 * UAT 0908 finding 12. TRBUD-152 made every query schema `.strict()` and the
 * guard that keeps them that way walks `parseQuery` CALL SITES — so a handler
 * that never calls `parseQuery` has no schema to be strict and sits outside the
 * registry entirely, permanently, with an empty allow-list still reading as
 * complete coverage. Four routes were in that gap and answered 200 to anything:
 *
 *     GET /api/reports/category-spend?timeframe=30d   -> 200, all-time data
 *     GET /api/assets/positions?bogus=1               -> 200
 *     GET /api/board?timeframe=30d                    -> 200
 *     GET /api/overview?bogus=1                       -> 200
 *
 * `timeframe` is the sharp one: it is a REAL parameter on the sibling
 * `/api/reports`, so a caller who had learnt it there was silently served
 * all-time figures under a 30-day heading.
 *
 * One shared schema rather than four empty ones, because "takes no parameters"
 * is one fact. It is deliberately NOT given a `.default()` or any field: parsing
 * it yields `{}`, and its only job is to refuse.
 */
export const noQuery = z.object({}).strict();
export type NoQuery = z.infer<typeof noQuery>;

/**
 * Starting a work stoppage — the writer `work_order_stoppage` never had.
 *
 * ── Why the shape mirrors four CHECKs ───────────────────────────────
 * Migration 0067 constrains this row four ways, and a caller that trips one gets a
 * bare 23514 with a constraint name in it. Every rule is restated here so the answer
 * is a sentence instead, and each message NAMES the constraint so the two cannot
 * drift silently apart.
 *
 * `stoppedAt` is deliberately not a parameter. The database defaults it to the
 * moment of the write, and letting a client choose it would let a mechanic backdate
 * an interruption — which is precisely the number the labour-ROI reporting and the
 * 11.40 audit comparison read.
 *
 * `recordedByUserId` is not a parameter either: it is the session, not a claim.
 */
export const startStoppageBody = strictObject({
    reason: z.enum(stoppageReasons),
    reasonNote: z.string().trim().max(1000).optional(),
    blockedOnPartId: z.string().uuid().nullish(),
    spawnedWorkOrderId: z.string().uuid().nullish(),
    idempotencyKey,
  })
  .superRefine((value, ctx) => {
    if (value.reason === "other" && (value.reasonNote ?? "").length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reasonNote"],
        message:
          "a stoppage reason of `other` needs a note saying what happened — " +
          "work_order_stoppage_note_chk enforces the same rule, and an unexplained " +
          "`other` teaches nobody anything",
      });
    }
    if (value.blockedOnPartId != null && value.reason !== "part_needed") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["blockedOnPartId"],
        message:
          "a blocked part only makes sense when the reason is `part_needed` — " +
          "work_order_stoppage_part_chk enforces the same rule",
      });
    }
    if (value.spawnedWorkOrderId != null && value.reason !== "another_fault_found") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["spawnedWorkOrderId"],
        message:
          "a spawned work order only makes sense when the reason is " +
          "`another_fault_found` — work_order_stoppage_spawn_chk enforces the same rule",
      });
    }
  });
export type StartStoppageBody = z.infer<typeof startStoppageBody>;

/**
 * Resuming a stoppage. No payload beyond the key.
 *
 * `resumedAt` is the database's `now()` for the same reason `stoppedAt` is: the
 * elapsed time is evidence, not an input. And there is nothing else to send —
 * `02-grants.sql` gives tb_app UPDATE on `resumed_at` and no other column, so the
 * reason and its evidence are frozen once recorded.
 */
export const resumeStoppageBody = strictObject({
  idempotencyKey,
});
export type ResumeStoppageBody = z.infer<typeof resumeStoppageBody>;

/**
 * Setting a work order's categories — the whole set, not a delta.
 *
 * REPLACE semantics, because that is what the grants allow: tb_app holds INSERT and
 * DELETE on `work_order_category_link` and no UPDATE, so re-tagging is a delete plus
 * an insert either way. Sending the intended final set makes the endpoint idempotent
 * by construction rather than by key.
 *
 * No enum. `work_order_category` is user-managed configuration (requirement 11.24),
 * so the ids are validated as uuids and their existence is the database's business —
 * a hard-coded list here would make a category somebody added unusable until the
 * front end shipped again.
 */
export const setWorkOrderCategoriesBody = strictObject({
  categoryIds: z.array(z.string().uuid()).max(MAX_WORK_ORDER_CATEGORIES),
  idempotencyKey,
});
export type SetWorkOrderCategoriesBody = z.infer<typeof setWorkOrderCategoriesBody>;

/**
 * Creating a category from the /config screen.
 *
 * `code` is deliberately NOT a parameter. It is the stable key `seed:config` matches
 * on, `02-grants.sql` withholds UPDATE on it precisely so a rename cannot break that
 * idempotency, and asking a person to invent one invites `Mech`, `mech` and
 * `mechanical-2`. The API derives it from the name — see `categoryCodeFromName`.
 */
export const createWorkOrderCategoryBody = strictObject({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(1000).optional(),
  isPreventive: z.boolean().optional(),
  idempotencyKey,
});
export type CreateWorkOrderCategoryBody = z.infer<typeof createWorkOrderCategoryBody>;

/**
 * Editing a category. Exactly the four columns `02-grants.sql` lets tb_app UPDATE.
 *
 * Deactivating via `active` is the only removal: a category that tagged closed work
 * is history, the link FK is ON DELETE RESTRICT, and tb_app holds no DELETE here.
 */
export const updateWorkOrderCategoryBody = strictObject({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(1000).nullish(),
  active: z.boolean().optional(),
  isPreventive: z.boolean().optional(),
  idempotencyKey,
});
export type UpdateWorkOrderCategoryBody = z.infer<typeof updateWorkOrderCategoryBody>;

// ── The flat config vocabularies (TRBUD-115 / UAT F-25) ──────────────
//
// Requirement 11.24 makes these vocabularies the operator's, not the schema's.
// Eden's D6 ruling of 3 September 2026 splits that promise BY SHAPE: the three
// FLAT lists below get a create-and-edit pair each, and the structured ones
// (`procedure` + `procedure_step`, `work_order_template`) get neither, because a
// POST/PATCH pair over a checklist-with-ordered-steps would misdescribe how it
// is actually edited. Their tb_app write grants were narrowed in the same change
// rather than left asserting an editor that does not exist.
//
// All three follow `work_order_category` exactly, and the three shared decisions
// are worth stating once:
//
//   1. **No DELETE, anywhere.** These tables tag history — `work_order.root_cause_id`,
//      `fault_observation.fault_code_id`, `asset_status_event.reason_id` — so
//      removal is a flag (`active` / `is_active`) and tb_app holds no DELETE.
//      `verify-schema.mjs` asserts that for all three.
//   2. **The stable key is DERIVED, never supplied.** `root_cause.code` and
//      `asset_status_reason.code` are what `seed:config` and the Sylectus
//      dispatch map match on, and `02-grants.sql` withholds UPDATE on both, so
//      asking a person to invent one invites `Wear`, `wear` and `wear-2`. The
//      API derives it from the label — see `categoryCodeFromName`.
//   3. **An UPDATE body carries exactly the columns `02-grants.sql` permits**,
//      and no more. A field here that the grant does not name is a form control
//      that fails with 42501 after the person has filled it in.

/**
 * Creating a root cause from /config.
 *
 * `code` is derived from the name, for reason 2 above: `seed:config` resolves the
 * five seeded causes by `lower(code)` (`root_cause_code_uq` is an expression
 * index), so a code that moved would re-create every row on the next seed run.
 */
export const createRootCauseBody = strictObject({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(1000).optional(),
  idempotencyKey,
});
export type CreateRootCauseBody = z.infer<typeof createRootCauseBody>;

/**
 * Editing a root cause. Exactly the three columns `02-grants.sql` lets tb_app
 * UPDATE — `code` is absent from the statement AND from the grant.
 */
export const updateRootCauseBody = strictObject({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(1000).nullish(),
  active: z.boolean().optional(),
  idempotencyKey,
});
export type UpdateRootCauseBody = z.infer<typeof updateRootCauseBody>;

/**
 * Creating a fault code from /config.
 *
 * `standard`, `spn`, `fmi` and `code` are the IDENTITY of a fault code and are
 * settable only here — `02-grants.sql` grants UPDATE on the other four columns
 * only, so a mistyped SPN is deactivated and re-entered rather than corrected.
 * That is deliberate: `fault_observation` rows already point at the row, and
 * re-pointing an SPN would silently re-label history the truck reported.
 *
 * ── The J1939 rule is checked HERE as well as by the CHECK ────────────
 * `fault_code_j1939_chk` requires both `spn` and `fmi` on a J1939 row. Left to
 * the database that arrives as a bare 23514 naming a constraint, so the refine
 * below says which two fields the form is missing. `fmi` tops out at 31 because
 * the standard gives it five bits. `spn` has no CHECK above `>= 0`, but the
 * column is a Postgres `integer`, so its type bounds it at 2,147,483,647: a
 * larger value would reach the INSERT and fail there as an unhandled 22003
 * rather than as a sentence. J1939 SPNs are 19 bits, far below either limit.
 */
/** `fault_code.spn` is a Postgres `integer`, so its type is its ceiling. */
export const FAULT_CODE_SPN_MAX = INT4_MAX;

export const createFaultCodeBody = strictObject({
    standard: z.string().trim().min(1).max(40),
    spn: z.number().int().min(0).max(FAULT_CODE_SPN_MAX).optional(),
    fmi: z.number().int().min(0).max(31).optional(),
    code: z.string().trim().min(1).max(60).optional(),
    label: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).optional(),
    defaultSeverity: z.enum(faultSeverities).optional(),
    idempotencyKey,
  })
  .refine((body) => body.standard.toUpperCase() !== "J1939" || body.spn !== undefined, {
    message: "A J1939 fault code needs an SPN.",
    path: ["spn"],
  })
  .refine((body) => body.standard.toUpperCase() !== "J1939" || body.fmi !== undefined, {
    message: "A J1939 fault code needs an FMI.",
    path: ["fmi"],
  })
  // Not a database constraint — the row would be legal. But a fault code with no
  // SPN/FMI pair and no `code` either is identified by nothing: the two unique
  // indexes are (standard, spn, fmi) and (standard, code), and a row satisfying
  // neither can be created twice over and told apart by nobody. The New fault
  // code dialog shows this message as written, so it is the instruction alone
  // and the reason stays here (TRBUD-331).
  .refine(
    (body) => body.code !== undefined || (body.spn !== undefined && body.fmi !== undefined),
    {
      message: "Give the fault code a code of its own, or an SPN and FMI pair.",
      path: ["code"],
    },
  );
export type CreateFaultCodeBody = z.infer<typeof createFaultCodeBody>;

/** Editing a fault code. Exactly the four columns `02-grants.sql` lets tb_app UPDATE. */
export const updateFaultCodeBody = strictObject({
  label: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(1000).nullish(),
  defaultSeverity: z.enum(faultSeverities).optional(),
  active: z.boolean().optional(),
  idempotencyKey,
});
export type UpdateFaultCodeBody = z.infer<typeof updateFaultCodeBody>;

/**
 * Creating an asset status reason from /config.
 *
 * `availability` is required and is stated ONCE, at creation: the column is
 * NOT NULL, no vendor field says whether a status means available, and
 * `02-grants.sql` does not grant UPDATE on it. So this is a human decision per
 * value, which is what `asset_status.ts` says it has to be.
 *
 * ── Why `availability` is not editable afterwards ────────────────────
 * Not because history would move — `asset_status_event` copies the value as it
 * stood, so it would not. Because the value is what makes the pair unable to
 * disagree: flipping `in_shop` to `active` would silently reclassify every
 * future shop visit as available, across the whole fleet, from a form field.
 * Retire the reason and add the replacement instead.
 *
 * `code` is derived from the label, and the derived code must satisfy
 * `asset_status_reason_code_chk` (`^[a-z][a-z0-9_]*$`) — the API refuses a label
 * whose code would start with a digit rather than letting 23514 surface.
 */
export const createStatusReasonBody = strictObject({
  label: z.string().trim().min(1).max(120),
  availability: z.enum(statusReasonAvailabilities),
  sortOrder: z.number().int().min(0).max(10000).optional(),
  idempotencyKey,
});
export type CreateStatusReasonBody = z.infer<typeof createStatusReasonBody>;

/**
 * Editing an asset status reason. Exactly the three columns `02-grants.sql` lets
 * tb_app UPDATE: `label`, `sort_order`, `is_active`.
 *
 * `isActive` is the removal mechanism and it is GUARDED on the server: the
 * Sylectus dispatch projector resolves `on_load`, `idle` and `dispatch_withheld`
 * by code and throws for the whole run if one is missing or retired, so
 * deactivating one of those three would stop dispatch projection with an error
 * naming a migration rather than the person who switched it off.
 */
export const updateStatusReasonBody = strictObject({
  label: z.string().trim().min(1).max(120).optional(),
  sortOrder: z.number().int().min(0).max(10000).optional(),
  isActive: z.boolean().optional(),
  idempotencyKey,
});
export type UpdateStatusReasonBody = z.infer<typeof updateStatusReasonBody>;

/**
 * Creating a procedure — the STRUCTURED half of D6 (TRBUD-170).
 *
 * ── One body carrying the whole checklist, not a step at a time ──────
 * The steps arrive WITH the procedure because that is the unit a person edits.
 * A POST that created an empty procedure and a second that appended steps to it
 * would leave a mandatory procedure with no steps committed and reachable in
 * between, and a mandatory procedure with zero required steps silently gates
 * nothing — the failure would be an exit gate that stopped working, which is
 * not a shape any caller should be able to produce. Hence `.min(1)`.
 *
 * ── There is no update body, and that is the ruling ──────────────────
 * tb_app holds INSERT and SELECT on `procedure` and `procedure_step`, and no
 * UPDATE or DELETE. Editing is therefore expressed as a new VERSION: omit
 * `version` — the server resolves it as `max(version) + 1` for the
 * `(workOrderTypeId, name)` pair, or 1 when the name is new. A caller cannot
 * choose a version, for the same reason it cannot choose a derived `code`
 * elsewhere in this file: the value has to agree with what is already stored.
 *
 * ── `sequence` is NOT a field ────────────────────────────────────────
 * Array order IS the order. The server renumbers `1..n` on insert, so a caller
 * cannot leave a gap, repeat a number, or start at zero —
 * `procedure_step_sequence_chk` requires `> 0` and
 * `procedure_step_procedure_sequence_uq` requires distinctness, and both would
 * otherwise arrive as a constraint name rather than as a sentence. It also
 * means the reorder control in the editor has nothing to keep in step: moving a
 * row in the array is the whole operation.
 *
 * `workOrderTypeId` is optional and means "every work-order type" when omitted,
 * which is how the column reads in `transition_work_order`
 * (`p.work_order_type_id IS NULL OR p.work_order_type_id = ...`). It is stated
 * once at creation and frozen: `02-grants.sql:548-550` keeps it out of any
 * future UPDATE grant, because re-pointing a procedure at another type would
 * silently change which jobs its gate blocks.
 */
export const createProcedureBody = strictObject({
    workOrderTypeId: z.string().uuid().optional(),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).optional(),
    isMandatory: z.boolean().default(false),
    steps: z
      .array(
        z
          .object({
            title: z.string().trim().min(1).max(200),
            instructions: z.string().trim().max(2000).optional(),
            isRequired: z.boolean().default(true),
          })
          .strict(),
      )
      .min(1)
      .max(200),
    idempotencyKey,
  })
  .strict();
export type CreateProcedureBody = z.infer<typeof createProcedureBody>;

/**
 * Correct a posted time entry — TRBUD-178, split from TRBUD-156 B8.
 *
 * ## Why a body at all, when the only variable is a number
 *
 * Because the number is not the only thing being asserted. `correct_time_entry`
 * writes three rows — the replacement entry, a reversal of the original's labour
 * cost, and a fresh labour cost — and the REASON is carried onto all three. A
 * correction that does not say why is the untraceable edit that
 * `work_order_time_entry_append_only` exists to prevent, wearing a correction's
 * name, so the reason is required in three places: here, as a `23514` inside the
 * routine, and as `work_order_time_entry_correction_reason_chk` on the column.
 *
 * ## No `userId`, no `workedAt`, no `timeKind` — and that is the shape
 *
 * All three are copied from the entry being corrected. Moving an entry to a
 * different person, day or kind is not a correction, it is two operations (void
 * this, log that), and each is separately auditable. `returnPartBody` makes the
 * same choice for the same reason, and says so in the same words.
 *
 * ## `.strict()`
 *
 * TRBUD-152's rule, and the stakes are `confirmInvoiceBody`'s: this body reaches
 * a money routine, and an ignored extra field is how a client comes to believe it
 * sent something that was never read. A caller that tries to smuggle a `userId` —
 * the field `logTimeBody` does accept — gets a 400 rather than a silently ignored
 * key and a correction attributed to the wrong person.
 *
 * `minutes` is bounded exactly as `logTimeBody.minutes` is: a positive integer of
 * at most 24 hours. Zero is refused rather than read as "delete it" —
 * `work_order_time_entry_minutes_chk` requires `minutes > 0`, so an entry that
 * should never have existed is corrected by reversing its cost, not by a zero
 * here. The routine restates both bounds.
 */
export const correctTimeBody = strictObject({
    minutes: z.number().int().positive().max(24 * 60),
    correctionReason: z.string().trim().min(3).max(1000),
    idempotencyKey,
  })
  .strict();
export type CorrectTimeBody = z.infer<typeof correctTimeBody>;

/* ── the 5 September reporting tables (TRBUD-141) ─────────────────── */

/**
 * `GET /reports/purchase-order-variance` — the PO-versus-invoice table.
 *
 * ## The filter is SERVER-SIDE, and that is the whole point of the parameter
 *
 * "Only lines that diverged" could be a `.filter()` in the browser, and it would
 * be wrong for two reasons that compound. The row cap would apply BEFORE the
 * filter, so a fleet with 400 clean lines and 3 bad ones would return 100 clean
 * rows and the screen would say there were no variances. And the count printed
 * beside the table would be the count of what was fetched rather than of what
 * exists, which is the class of defect the whole gold tier is a reaction to.
 *
 * ## `.strict()`, per TRBUD-152
 *
 * A zod object strips unknown keys, so `?onlyVariances=true` — the plural, which
 * is what anybody types — would be DISCARDED and the endpoint would answer 200
 * with every line, filtered by nothing. That reads as "no divergences worth
 * separating out", which is the F-37 shape: a 200 that looks like an answer and
 * is the absence of one.
 *
 * ## `queryBoolean`, not `z.coerce.boolean()`
 *
 * See that export's own note: every query parameter arrives as a string and
 * `Boolean("false")` is `true`. The default is `false`, so an ABSENT key means
 * unfiltered — nothing here is a gate keyed on presence, and a cold database
 * renders as an empty table rather than as a refusal.
 */
export const purchaseOrderVarianceQuery = z
  .object({
    onlyVariance: queryBoolean.default(false),
    limit: z.coerce.number().int().min(1).max(500).default(200),
  })
  .strict();
export type PurchaseOrderVarianceQuery = z.infer<typeof purchaseOrderVarianceQuery>;

// ── Home base mapping (TRBUD-171) ────────────────────────────────────
//
// The table a person maintains to say which Sylectus drop yard, city or
// unit-number suffix means which CES terminal. The projector reports the names
// it could not place; these are how somebody answers it.

/**
 * Add a mapping row.
 *
 * `matchKey` is NOT a parameter. It is derived from `matchLabel` by
 * `seedMatchKey` on the server, for the reason the derived `code` fields above
 * are also absent: the normalisation is what makes `Continental Expedited
 * Services- Ckv Terminal` and `Continental Expedited Services-Ckv Terminal` one
 * row instead of two, and a hand-typed key could desynchronise from its label and
 * match nothing.
 *
 * `basis` excludes `manual` because `asset_home_base_map_basis_chk` does — a way
 * of deciding a PLACE is not the same thing as one person's ruling about one
 * asset.
 *
 * The `unit_suffix` pairing is refined rather than left to the database, because
 * `asset_home_base_map_suffix_basis_chk` would otherwise arrive as a bare
 * constraint name after the user has filled the form in.
 */
export const createHomeBaseMapBody = strictObject({
    matchKind: z.enum(homeBaseMatchKinds),
    /**
     * The human spelling — `Continental Expedited Drop Yard`, `LAREDO, TX`,
     * `-DTW`. 200 rather than 120: the landed drop names run long
     * (`Buske Logistics (Dtw Trailer Drop Yard)`).
     */
    matchLabel: z.string().trim().min(1).max(200),
    locationId: z.string().uuid(),
    basis: z.enum(homeBaseMapBases),
    notes: z.string().trim().max(1000).optional(),
    idempotencyKey,
  })
  .strict()
  .refine((v) => (v.matchKind === "unit_suffix") === (v.basis === "unit_suffix"), {
    message:
      "A unit-number suffix always carries the 'unit_suffix' basis, and no other kind of key may use it.",
    path: ["basis"],
  });
export type CreateHomeBaseMapBody = z.infer<typeof createHomeBaseMapBody>;

/**
 * Edit a mapping row.
 *
 * `matchKind` and `matchLabel` are absent, and that is the whole shape of the
 * thing: they determine `match_key`, which is the business key that every
 * already-written `asset.domicile_basis` was decided by. Re-pointing a key at a
 * different meaning is a NEW row plus a deactivation, not an edit — otherwise the
 * history of why an asset is based where it is quietly changes underneath it.
 * `02-grants.sql` agrees: tb_app holds no UPDATE on either column.
 *
 * What IS editable is where the row points and how strong a claim it makes: a
 * customer's dock that CES later leases becomes `own_lot` without needing a new
 * row, because it is the same place under the same name.
 */
export const updateHomeBaseMapBody = strictObject({
    locationId: z.string().uuid().optional(),
    basis: z.enum(homeBaseMapBases).optional(),
    active: z.boolean().optional(),
    notes: z.string().trim().max(1000).nullish(),
    idempotencyKey,
  })
  .strict();
export type UpdateHomeBaseMapBody = z.infer<typeof updateHomeBaseMapBody>;

/* ── TRBUD-154 slice 2 — the category drill-down ──────────────────── */

/**
 * `GET /reports/category-spend/:categoryId/work-orders` — the work orders behind
 * one category row.
 *
 * ## `limit` and NOTHING else, and the absences are the design
 *
 * **No timeframe.** The segment at the top of the Reports screen windows the KPI
 * cards; the category table covers all time and its own caveat says so. Giving
 * that control a second meaning down here is how a reader ends up comparing a
 * drill-down measured over 30 days against a summary measured over all time and
 * concluding the summary is wrong. The row this opens from has no timeframe, so
 * neither does this.
 *
 * **No sort.** The order is cost descending, fixed, with the work-order number as
 * the tiebreak so it is total rather than planner-dependent. A configurable sort
 * over a capped page is a way of changing WHICH rows come back while looking like
 * a way of changing their order.
 *
 * **No item filter.** Slice 3 is the filtering slice and is a separate branch; a
 * filter shipped here would be a second vocabulary for the same idea that would
 * then have to be reconciled with it.
 *
 * ## `.strict()`, per TRBUD-152
 *
 * `?categoryId=...` — the key a reader would guess, given the path already
 * carries it — is a 400 naming the key rather than a 200 that silently ignored
 * it. So is `?limit=10&timeframe=30d`, which is the specific mistake this
 * schema's own reasoning above invites somebody to make.
 */
export const categoryWorkOrdersQuery = z
  .object({
    limit: z.coerce.number().int().min(1).max(500).default(100),
  })
  .strict();
export type CategoryWorkOrdersQuery = z.infer<typeof categoryWorkOrdersQuery>;

/**
 * `GET /reports/work-orders/:workOrderId/cost-lines` — the itemised lines.
 *
 * ## An EMPTY strict object, which is a stronger statement than no schema at all
 *
 * There is genuinely nothing to configure, and the absence of a `limit` is
 * load-bearing rather than an oversight: the response publishes the work order's
 * subtotal from `v_work_order_cost_total` beside the lines, and a PAGE of lines
 * under a total of ALL of them would not add up. Truncating this list would break
 * the one property the drill-down exists to demonstrate. A work order's ledger is
 * bounded by how much work was done on it, so there is no page to turn.
 *
 * The alternative was no `parseQuery` call at all — which is what
 * `GET /reports/category-spend` does, correctly, because it takes no parameters
 * either. Here the schema earns its line: a caller who reasons "the sibling route
 * takes a limit, so this one must too" gets a 400 naming `limit` rather than a
 * silently complete list they then quote as a page. That is F-37's rule applied
 * to a parameter that does not exist, rather than to one that was misspelled.
 */
export const workOrderCostLinesQuery = z.object({}).strict();
export type WorkOrderCostLinesQuery = z.infer<typeof workOrderCostLinesQuery>;

/**
 * Void a posted TIME entry — "this should never have existed". TRBUD-183.
 *
 * ## No duration field, and there must never be one
 *
 * `correctTimeBody` carries `minutes` because a correction says the FIGURE was
 * wrong. A void says the RECORD was, so there is no replacement figure to type.
 * That is also why a void cannot be expressed as a correction: `minutes` is
 * `positive()` and `work_order_time_entry_minutes_chk` is `> 0`, so the smallest
 * thing a correction can reduce an entry to is one minute of work that never
 * happened.
 *
 * ## The reason is required in four places
 *
 * `min(3)` here, a 23514 inside `void_time_entry`, `ledger_void_reason_chk` on
 * the column, and `void_reason text NOT NULL`. The floor matches `reverseCostBody`
 * and `correctTimeBody`, so all three "somebody made a mistake" paths hold the
 * same line — the reason is the whole difference between a void and a deletion,
 * and this is the only field standing there.
 */
export const voidTimeBody = strictObject({
    voidReason: z.string().trim().min(3).max(1000),
    idempotencyKey,
  })
  .strict();
export type VoidTimeBody = z.infer<typeof voidTimeBody>;

/**
 * Void a posted COST — the money half of the same design. TRBUD-183.
 *
 * Identical in shape to `voidTimeBody`, deliberately: Eden ruled ONE design for
 * both ledgers, so two bodies that differed would be two designs. They are
 * separate types rather than one shared schema because they are separate routes
 * behind separate routine permissions (`void_time_entry` / `void_cost_entry`),
 * and an administrator may reasonably allow one and not the other — a shared
 * type would invite a shared route helper and then a shared permission.
 *
 * No amount, for the same reason `reverseCostBody` has none: nothing about the
 * figure is the caller's to state. A void does not compensate the entry, it
 * removes it from every total, so there is no arithmetic to get wrong.
 */
export const voidCostBody = strictObject({
    voidReason: z.string().trim().min(3).max(1000),
    idempotencyKey,
  })
  .strict();
export type VoidCostBody = z.infer<typeof voidCostBody>;

/**
 * Erase a work-order comment's words (migration 0268, the comment eraser).
 *
 * A reason is required, and it is what stands in the words' place, so the form
 * asks the admin not to repeat what they are erasing. The idempotency key makes
 * a retried request return the first erasure rather than an error: an erasure
 * happens once and cannot be undone.
 */
export const eraseCommentBody = strictObject({
    reason: z.string().trim().min(3).max(1000),
    idempotencyKey,
  })
  .strict();
export type EraseCommentBody = z.infer<typeof eraseCommentBody>;

/**
 * Record that a vendor was PAID, and say which invoices it settles — TRBUD-184.
 *
 * ## Why this did not exist
 *
 * `record_payment` has been a granted SECURITY DEFINER routine since the finance
 * migrations and had **never had a caller**. What had been satisfying
 * `db:verify-routines`' no-caller check was the permission KEY of the same name
 * in `vocabulary.ts`, not any call — TRBUD-175 stopped counting a mention as a
 * caller and the gap surfaced. So TruckBuddy could record a vendor invoice and
 * reconcile a deposit, and `v_vendor_outstanding_balance` could only ever grow.
 *
 * ## Two functions, one act
 *
 * `record_payment` writes the payment; `allocate_payment` says which invoice it
 * settles. Their own bodies call this out — *"same as record_payment — two halves
 * of one act"* — and a payment recorded with no allocation moves no balance, so
 * offering the two as separate screens would let somebody do half the job and see
 * nothing happen. One request does both, inside one transaction.
 *
 * ## `allocations` is OPTIONAL, and that is a cold-start decision
 *
 * A payment can legitimately arrive before anybody has decided what it settles —
 * a lump sum against a statement, a payment on account. Refusing to record it
 * until an invoice is named would make the ledger describe less than the
 * business knows, and CLAUDE.md's rule is that absence warns and allows. So an
 * empty list is accepted and the read path reports the payment as UNALLOCATED
 * rather than hiding it.
 *
 * Note that is not merely a display choice: `v_vendor_unallocated_deposit`
 * filters `payment_kind = 'deposit'`, so a `payment`-kind row with no allocation
 * appears in NO view at all. The read path added with this route is the only
 * place it can be seen.
 *
 * ## `amount` is `decimalString`, matched to the column
 *
 * `payment.amount` is `numeric(10,2)` and `payment_amount_chk` requires it to be
 * positive. `decimalString` is the scale-2 validator and is the right one here —
 * unlike `purchase_order_line.unit_cost`, which is `numeric(10,4)` and needed its
 * own. Match the validator to the COLUMN.
 */
/**
 * Reverse a stock movement that should not have been recorded — TRBUD-180.
 *
 * ## Not the same act as `returnPartBody`, and not a superset of it
 *
 * `return_part_to_stock` says *the part came back*: a quantity of an ISSUE
 * returns to the shelf it left, bounded by what went out. This says *the row is
 * wrong* — a receipt for a delivery that never arrived, an issue against the
 * wrong work order, an adjustment typed with the wrong sign. Nothing came back.
 *
 * That is why there is **no `quantity`**. `reverse_inventory_txn` compensates the
 * whole row, and the whole transfer group when the row is one leg of a transfer.
 * A partial reversal would be a different assertion — that some of the movement
 * happened — and the honest way to say that is an `adjust`, not a half-undo.
 *
 * ## `reason` is required, to the same 3-character floor as its siblings
 *
 * `reverseCostBody`, `returnPartBody` and `voidCostBody` all hold this line. A
 * correction says an earlier entry was wrong, and an unexplained correction in an
 * append-only ledger is the one thing a later reader cannot reconstruct — the
 * reversal row is permanent, so the sentence is the only account of why it
 * exists.
 *
 * ## `inventoryTxnId` is a STRING
 *
 * `inventory_txn.id` is a bigint. A JSON number would lose precision above 2^53,
 * and `=== 1042` on the wire value is false regardless — the defect TRBUD-185
 * went through twenty read paths to remove.
 */
export const reverseMovementBody = strictObject({
    inventoryTxnId: bigintIdString("expected a ledger id"),
    reason: z.string().trim().min(3).max(500),
    idempotencyKey,
  })
  .strict();
export type ReverseMovementBody = z.infer<typeof reverseMovementBody>;

export const recordVendorPaymentBody = strictObject({
    amount: decimalString,
    currency: currencyCodeDefaulted,
    /**
     * When the money actually left, not when somebody typed it in.
     *
     * `payment.paid_at` is `timestamptz NOT NULL` and the routine takes it as a
     * parameter rather than defaulting to `now()`, which is the whole point: a
     * payment entered on Tuesday for a cheque sent on Friday is a Friday
     * payment, and a default would quietly make it a Tuesday one.
     */
    paidAt: z.string().datetime({ offset: true }),
    /** The cheque number, the transfer id — whatever lets somebody find it again. */
    reference: z.string().trim().min(1).max(200).nullish(),
    allocations: z
      .array(
        z.object({
          vendorInvoiceId: z.string().uuid(),
          amount: decimalString,
        }),
      )
      // A cap rather than none, matching the other list-bearing bodies. Fifty
      // invoices settled by one payment is already an unusual month.
      .max(50)
      .default([]),
    idempotencyKey,
  })
  .strict();
export type RecordVendorPaymentBody = z.infer<typeof recordVendorPaymentBody>;

/*
 * ── The two RFP requirement writers — TRBUD-187 remedy (a) ───────────
 *
 * Eden ruled on 8 September 2026 that `builtin:vendor` and `builtin:meter_reading`
 * get real writers, and that custom-field VALUES stay deferred.
 *
 * The gap these close is the softlock TRBUD-187's guard describes from the other
 * side. `tb_app` has held column-level `UPDATE (primary_vendor_id)` and
 * `UPDATE (meter_reading_id)` on `work_order` all along — verified against
 * `information_schema.column_privileges`, not assumed — and NO route used
 * either. So a stage that required one refused every forward move for ever, with
 * nothing an operator could press, and the requirements panel could only say
 * "no route writes this".
 *
 * The guard stays and is not made redundant: it simply has two fewer unwritable
 * builtins to catch, and `requirementHasWriter` derives that from the registry
 * rather than from a second list, so adding the entries there is the only change
 * needed to let a stage require them.
 *
 * Both take a NULLABLE id, because clearing is a legitimate act — a vendor
 * chosen in error must be removable, and a field that can be set but never unset
 * is a one-way door. The stage gate then reports it missing again, which is the
 * honest consequence rather than a refusal at the route.
 */
export const setWorkOrderVendorBody = strictObject({
  /** Null clears it. `work_order.primary_vendor_id` is nullable. */
  vendorId: z.string().uuid().nullable(),
  idempotencyKey,
});
export type SetWorkOrderVendorBody = z.infer<typeof setWorkOrderVendorBody>;

/*
 * ── The other vendors on a work order — TRBUD-304 ────────────────────
 *
 * Matt Farless, 25 September 2026: *"Is there a way to add multiple vendors?"*,
 * *"to a WO"*. Eden ruled the same day: the primary vendor stays the lead (the
 * `builtin:vendor` gate, the queue and the VENDOR tag keep reading
 * `primary_vendor_id`), and other vendors are ADDED beside it, each showing what
 * it has billed on the job. `work_order_vendor` (migration 0265) holds them.
 *
 * Adding takes a vendor that is not already on the job and is not blacklisted or
 * inactive. Removing is by the added row's own id, like unassigning, and is
 * recorded rather than deleted.
 */
export const addWorkOrderVendorBody = strictObject({
  vendorId: z.string().uuid(),
  idempotencyKey,
});
export type AddWorkOrderVendorBody = z.infer<typeof addWorkOrderVendorBody>;

export const removeWorkOrderVendorBody = strictObject({ idempotencyKey });
export type RemoveWorkOrderVendorBody = z.infer<typeof removeWorkOrderVendorBody>;

export const setWorkOrderMeterReadingBody = strictObject({
  /**
   * An existing `asset_meter_reading` row, or null to clear.
   *
   * An ID rather than a number-and-unit, deliberately: `work_order.meter_reading_id`
   * is a foreign key to a reading that was actually OBSERVED, with its own source
   * and timestamp. Accepting a bare figure here would mint an observation nobody
   * made, which is the opposite of what the column is for — and Samsara has the
   * final say on total miles and engine hours.
   *
   * A DECIMAL STRING, not a uuid and not a number. `asset_meter_reading.id` is a
   * `bigint identity`, and this repo's rule (TRBUD-185) is that a bigint crosses
   * the wire as text — `postgres.js` returns int8 as a string and `createSql`
   * parses nothing, so typing it as a JS number would put back the precision
   * hazard those casts exist to remove. Same shape as
   * `createVendorInvoiceBody.costEntryIds`.
   */
  meterReadingId: bigintIdString("expected a meter reading id").nullable(),
  idempotencyKey,
});
export type SetWorkOrderMeterReadingBody = z.infer<typeof setWorkOrderMeterReadingBody>;

/**
 * A unit cost that must be a real price — greater than zero. TRBUD-209.
 *
 * `unitCostString` on its own admits `"0"`, and for `POST /parts` that is
 * correct: creating a part with an unknown price is a legitimate bulk act and
 * the route warns that the part "can be received into stock but not issued".
 *
 * It is NOT correct for the writer below, and the reason is the valuation
 * policy rather than taste. `partUnitCost` treats zero as **no answer** — six
 * MaintainX parts arrive at 0, including a grease Eden judged to be bad vendor
 * data — so `POST /parts/issue` refuses a zero-priced part exactly as it
 * refuses a null-priced one. Accepting `"0"` here would report success and
 * leave the operator facing the identical refusal a second time, with nothing
 * on screen to explain why the thing they had just set had not taken.
 *
 * So this refuses the one value that would be a silent no-op. `numeric(10,4)`
 * scale and range still come from `unitCostString`.
 */
export const positiveUnitCostString = unitCostString.refine(
  (v) => Number.parseFloat(v) > 0,
  "a unit cost must be greater than zero — zero reads as an unknown price, not a free part",
);

/**
 * Set a catalogue part's unit cost — `PATCH /parts/:id`. TRBUD-209.
 *
 * **`unitCost` is the only field, and the object is `.strict()`.** Both halves
 * are deliberate. This is not a general part editor: `unit_cost` is the one
 * column on `part` that `tb_app` may not write, and it reaches the column
 * through a SECURITY DEFINER routine under an admin-only permission key. A
 * `PATCH` that also carried `description` or `isStocked` would mix a
 * function-written financial input with ordinary app-role columns in one
 * request, and the audit row could no longer say that a price change was the
 * thing that happened. Editing the rest of a part is a separate route nobody
 * has asked for yet.
 *
 * Strict per TRBUD-199, so an unknown key is a 422 rather than a silently
 * ignored field — the shape that lets a client believe it set something it did
 * not.
 */
export const setPartUnitCostBody = strictObject({
  unitCost: positiveUnitCostString,
  idempotencyKey,
});
export type SetPartUnitCostBody = z.infer<typeof setPartUnitCostBody>;

/**
 * Record a later manual load-status observation on a work order — DEC-01.
 *
 * Brett's ruling: *"Allow a later MANUAL toggle (freight recovered by another
 * truck) with no further Sylectus call."* So this route makes **no vendor call
 * at all** — the value comes from the person in front of the truck, which is the
 * only source that knows a load was recovered.
 *
 * ── WHAT THIS CANNOT DO, BY CONSTRUCTION ────────────────────────────
 *
 * It cannot touch the reading taken at creation. There is no field here for
 * `basis` or `observedAt` and no route that accepts one: the three
 * `load_status_at_open*` columns are absent from `tb_app`'s column-level UPDATE
 * grant on `work_order`, so the API's own login cannot write them however this
 * body is spelled. The toggle appends to `work_order_load_status_event` and the
 * stamp stays as dispatch left it — which is what makes "how many breakdowns
 * happened while the truck was loaded" answerable after a recovery.
 *
 * `unknown` is accepted alongside the two definite values, because a person
 * correcting a wrong reading to "we genuinely do not know" is recording
 * something true, and forcing them to pick a side would put a guess in the
 * ledger. The enum is spelled literally rather than imported from
 * `load-status.ts` for the reason every other schema in this file spells its
 * own: `z.enum` needs a mutable tuple and the vocabulary is `as const`, and a
 * test asserts the two agree.
 */
export const recordWorkOrderLoadStatusBody = strictObject({
  state: z.enum(["on_load", "not_on_load", "unknown"]),
  /**
   * Why — *"freight recovered by another truck"*. Optional, rendered verbatim;
   * nothing parses it.
   */
  note: z.string().trim().max(1000).nullish(),
  idempotencyKey,
});
export type RecordWorkOrderLoadStatusBody = z.infer<typeof recordWorkOrderLoadStatusBody>;

/**
 * Warranty on one vendor's work on a work order — 7 October 2026, replacing the
 * single "covered by warranty" flag of TRBUD-237.
 *
 * Its own audited route rather than a field on a generic patch, which is the
 * ruling the comment beside `PATCH /work-orders/:id/vendor` records: "who marked
 * this repair as warranty work, and when?" is exactly the question a warranty
 * claim turns on.
 *
 * `covered` is a plain required boolean, not `.optional()`: the control is a
 * checkbox with two states, so an absent key would have to mean "leave it
 * alone", an ambiguity the checkbox cannot produce. `note` is what the warranty
 * covers; the route stores it only on a covered vendor, and an empty one as
 * null.
 */
export const setWorkOrderVendorWarrantyBody = strictObject({
  covered: z.boolean(),
  note: z.string().trim().max(WARRANTY_NOTE_MAX).nullable(),
  idempotencyKey,
});
export type SetWorkOrderVendorWarrantyBody = z.infer<typeof setWorkOrderVendorWarrantyBody>;

/**
 * The custom-field values on a work order — `PATCH /work-orders/:id/custom-fields`,
 * TRBUD-249.
 *
 * ## The WHOLE object, never a partial merge
 *
 * `tb_validate_custom_fields` fires `BEFORE UPDATE OF custom_fields` and
 * validates `NEW.custom_fields` **entire**: every key must be in the work order's
 * frozen schema, every value must match its declared type, and every active
 * required field must be PRESENT. Those are properties of the finished object,
 * not of a change to it, so a merge endpoint would be computing the object the
 * trigger judges somewhere the trigger cannot see — and "clear this field" would
 * have no spelling at all, because an absent key would already mean "leave it".
 * Sending the object the caller intends to end up with makes the request and the
 * rule the same shape.
 *
 * The cost is the ordinary one for a replace: two people editing different fields
 * of one work order last-writer-wins. That is the same exposure every other field
 * on this screen has and is not worth a version column here.
 *
 * ## `z.unknown()` rather than a union
 *
 * The three legal value shapes are boolean, number and ISO date string, and the
 * trigger already refuses everything else with a message that names the field and
 * the type it wanted. Restating that union here would give two validators with
 * one authority between them, and the zod one would be the one that drifted: the
 * definition is a FROZEN jsonb snapshot, so an old work order can legitimately
 * carry a shape today's `customFieldDataTypes` no longer lists. The route surfaces
 * the trigger's own sentence instead.
 */
export const setWorkOrderCustomFieldsBody = strictObject({
  customFields: z.record(z.string(), z.unknown()),
  idempotencyKey,
});
export type SetWorkOrderCustomFieldsBody = z.infer<typeof setWorkOrderCustomFieldsBody>;

/**
 * Closing a MaintainX-imported work order the old system already finished, per
 * row — `POST /work-orders/:id/close-imported`, TRBUD-244.
 *
 * `effectiveAt` is a FALLBACK and not an override, which is why it is optional
 * and why the field is named for what it is. `close_imported_work_order` prefers
 * MaintainX's own `completedAt` out of the bronze payload, then the work order's
 * imported `completed_at`, and only reaches for this when neither exists. A
 * caller cannot use it to restate a vendor instant that is on record, and the
 * basis it lands on comes back in the response so which one was used is a fact
 * rather than an assumption.
 *
 * No `reason`. The reason is not the operator's to write: the routine composes it
 * from MaintainX's own `raw_status` and the instant basis, so every one of these
 * events says the same true thing in the same words and a thousand of them stay
 * countable. Contrast `POST /cancel`, where the reason IS the point.
 */
export const closeImportedWorkOrderBody = strictObject({
  effectiveAt: z.string().datetime({ offset: true }).optional(),
  idempotencyKey,
});
export type CloseImportedWorkOrderBody = z.infer<typeof closeImportedWorkOrderBody>;

/**
 * The bulk sweep — `POST /work-orders/close-imported`, TRBUD-244.
 *
 * **`apply` defaults to FALSE, and that default is the safety.** Eden's ask was a
 * button that clears 1,731 rows; a body that forgets the flag has to report, not
 * close. The dry run and the apply return the same shape so the confirmation
 * dialog can render both, and the ids it would touch come back either way.
 *
 * `limit` bounds the id list in the RESPONSE, not the work: an apply closes every
 * eligible row and says `truncated` when it listed fewer than it did. Sizing the
 * work by a page would make "close all" mean "close some", which is the thing the
 * operator would least expect from a button labelled that way.
 */
export const closeImportedWorkOrdersBody = strictObject({
  apply: z.boolean().default(false),
  /** Narrow the sweep to one pipeline. Absent means every work-order type. */
  typeId: z.string().uuid().optional(),
  limit: z.number().int().min(1).max(500).default(50),
  idempotencyKey,
});
export type CloseImportedWorkOrdersBody = z.infer<typeof closeImportedWorkOrdersBody>;

/**
 * Preview the app as another role — `POST /session/preview-role`, TRBUD-253.
 *
 * `null` clears the preview, which is why the field is `.nullable()` and not
 * `.optional()`: "exit preview" has to be expressible, and an absent key would be
 * indistinguishable from a client that forgot to send one.
 *
 * The enum is the vocabulary and nothing more. WHO may name WHICH role is decided
 * in the database by `tb_auth_set_preview_role` against `user_role`, because a
 * body schema cannot know who is sending it — which is the mistake the persona
 * header made and the reason B4 says the role switcher must not ship.
 */
export const setPreviewRoleBody = strictObject({
  role: z.enum(userRoles).nullable(),
  idempotencyKey,
});
export type SetPreviewRoleBody = z.infer<typeof setPreviewRoleBody>;

/**
 * Place an unclassified asset by hand — `PATCH /assets/:id/class`, plan 2.1.
 *
 * `assetClasses`, not `assetClassFilters`: `unclassified` is a way of LOOKING at
 * the fleet, never a class to write, and the CHECK on `asset.asset_class` admits
 * exactly `assetClasses` (four since TRBUD-348). Clearing a class is deliberately not offered — nothing
 * downstream benefits from a unit being un-placed, and the twelve this exists for
 * are already in that state.
 */
export const setAssetClassBody = strictObject({
  assetClass: z.enum(assetClasses),
  idempotencyKey,
});
export type SetAssetClassBody = z.infer<typeof setAssetClassBody>;

/**
 * The four profile fields a person may revert to the vendors' keeping.
 *
 * Exactly the four that carry a `<field>_basis` column (migration 0248), which
 * is the same as saying: exactly the four the profile projector UPDATEs.
 * `assetType` and `name` are absent because they have no basis to clear — the
 * identity projector writes them on INSERT only, so there is no vendor value
 * waiting to come back and "revert" would be a button that did nothing.
 */
export const assetProfileRevertableFields = [
  "make",
  "model",
  "modelYear",
  "ownership",
] as const;
export type AssetProfileRevertableField = (typeof assetProfileRevertableFields)[number];

/**
 * Correct what a vendor got wrong — `PATCH /assets/:id/profile`.
 *
 * Eden, 21 September 2026: *"we need to add an edit asset information page and
 * button for the assets so we can manually enter details that sylectus, or
 * maintainx, or samsara get wrong occasionally."*
 *
 * ## Omission is not the same as null, and this one has teeth
 *
 * A field OMITTED leaves the column AND its basis exactly as they are; a field
 * sent as `null` CLEARS the value and records `'manual'`, which is an assertion
 * that the vendor's value was wrong and nobody knows the right one. So every
 * field is `nullish()` and the route keys on `hasOwnProperty`, never on
 * truthiness.
 *
 * That distinction is not a nicety. `PATCH /vendors/:id/profile` keys on
 * `hasOwnProperty` precisely because a `null` sent for an unmentioned field
 * would have irreversibly wiped about 25 of Matt's phone-column notes, and the
 * dialog here sends a whole form. The route's dbtest asserts the key is ABSENT
 * from the statement rather than merely falsy, because a patch object holding
 * `make: null` and one holding no `make` at all are both falsy and only one of
 * them is correct.
 *
 * ## `unitNumber` is accepted by this schema ONLY so it can be refused properly
 *
 * It is not editable (see the route), but `strictObject` would reject it with a
 * generic "unrecognized key" 400 and the person sending it would learn nothing.
 * It is the field a user most wants to "fix" — they are trying to get the
 * letters of `51378HD` onto a screen — and the useful answer is that the letters
 * already work and where they come from. So it parses, and the route refuses it
 * with that sentence. Deliberately typed loosely: it is never read as a value,
 * only detected.
 *
 * ## What is not here, and why each is a refusal rather than an omission
 *
 * `assetClass` belongs to `PATCH /assets/:id/class`, which already owns the
 * column with its own basis and tests — two writers on one column buys nothing.
 * Odometer and engine hours live in `meter_reading` and Samsara has final say on
 * both. Dispatch status is `asset_status_event`, which no route under
 * `apps/api/src/routes/` may write (TRBUD-38, enforced by
 * `dispatch-write-boundary.test.ts`). `current_location_id` is Samsara's, and a
 * wrong terminal is corrected through `asset_home_base_map`. `status` and
 * `retired_at` belong to `POST /assets/:id/lifecycle`. VIN, serial and plate are
 * rows in the temporal `asset_identifier`, where `tb_app` holds UPDATE on
 * `valid_during` and `is_current` and NOT on `value_raw` — correcting one is
 * "supersede and replace", not an edit, and it needs TRBUD-103 (duplicate
 * current VINs are accepted today) decided first.
 *
 * ## Bounds are the creation schema's, not new ones
 *
 * `make` and `model` at 80, `assetType` and `name` at 120, the year at
 * 1900–2100: every one copied from `createAssetBody` above, which writes the
 * same columns. The rule is the one `vendorAddressFields` states — two schemas
 * writing one column must not drift, "or a value accepted at creation is
 * rejected on edit — or worse, the reverse". The year bound is also the CHECK on
 * `asset.model_year`.
 */
export const assetProfileBody = strictObject({
  make: z.string().trim().max(80).nullish(),
  model: z.string().trim().max(80).nullish(),
  modelYear: z.number().int().min(1900).max(2100).nullish(),
  ownership: z.enum(ownerships).nullish(),
  assetType: z.string().trim().max(120).nullish(),
  name: z.string().trim().max(120).nullish(),
  /**
   * Hand these fields back to the vendors: set `<field>_basis` to `'ingest'` and
   * leave the value alone, so the next profile projection may fill or overwrite
   * it again.
   *
   * Without this a correction is a ONE-WAY DOOR: a person who fixes a make by
   * mistake has told every future projection to stop correcting that column, for
   * ever, and no screen would offer a way back. The value is deliberately NOT
   * cleared — "stop defending this field" is a different instruction from "this
   * field is empty", and blanking it would lose a good value on a unit no vendor
   * currently reports.
   */
  revert: z.array(z.enum(assetProfileRevertableFields)).max(4).optional(),
  /**
   * Present only to be refused with an explanation — see the note above. Never
   * read as a value.
   */
  unitNumber: z.unknown().optional(),
  idempotencyKey,
});
export type AssetProfileBody = z.infer<typeof assetProfileBody>;

/* ── Work-order itemization: issues and packages ─────────────────────────
 *
 * A VISIT (a work order of the `visit` container type) carries ISSUES, and a
 * manager routes groups of them into PACKAGES — child work orders of the
 * in-house or vendor pipeline. These bodies are the writers for that. The
 * pipeline decision is `createWorkPackageBody.typeId`, made per package at
 * routing time and never on the ticket; `createWorkOrderBody.issues` (declared
 * beside that body, above) lets the ticket be written with its list in the
 * same submit. `workOrderIssueInput` and `MAX_WORK_ORDER_ISSUES` live up there
 * for the module-load ordering reason recorded on them.
 */

export const addWorkOrderIssuesBody = strictObject({
  issues: z.array(workOrderIssueInput).min(1).max(MAX_WORK_ORDER_ISSUES),
  idempotencyKey,
});
export type AddWorkOrderIssuesBody = z.infer<typeof addWorkOrderIssuesBody>;

/**
 * Edit one issue. `status` moves it between open / resolved / withdrawn;
 * `packageWorkOrderId: null` takes it back out of a package (only while that
 * package is still at its entry stage — the route enforces that). At least one
 * field besides the key must be present, or the request says nothing.
 */
export const updateWorkOrderIssueBody = strictObject({
  status: z.enum(workOrderIssueStatuses).optional(),
  description: z.string().trim().min(1).max(500).optional(),
  reasonTypeId: z.string().uuid().nullish(),
  packageWorkOrderId: z.null().optional(),
  idempotencyKey,
}).refine(
  (value) =>
    value.status !== undefined ||
    value.description !== undefined ||
    value.reasonTypeId !== undefined ||
    value.packageWorkOrderId !== undefined,
  { message: "an issue update names at least one field to change" },
);
export type UpdateWorkOrderIssueBody = z.infer<typeof updateWorkOrderIssueBody>;

/**
 * Route a group of issues: ONE package, ONE pipeline, at most ONE worker.
 *
 * One worker is not a UI nicety. `applyStageLabour` charges EVERY mechanic
 * assigned during an `accrues_labour` stage for his assigned part of it when the
 * package leaves the stage (TRBUD-403), so two mechanics assigned together on one
 * package are the same minutes billed twice.
 * A second person is a second package. The vendor arm takes a contact and,
 * optionally, the vendor itself as the package's primary vendor.
 *
 * `title` defaults server-side to the first issue's description, so the queue
 * row reads as the job and not as "Package 2".
 */
export const createWorkPackageBody = strictObject({
  typeId: z.string().uuid(),
  issueIds: z.array(z.string().uuid()).min(1).max(MAX_WORK_ORDER_ISSUES),
  title: z.string().trim().min(1).max(500).optional(),
  userId: z.string().uuid().nullish(),
  vendorContactId: z.string().uuid().nullish(),
  primaryVendorId: z.string().uuid().nullish(),
  priority: z.enum(priorities).optional(),
  dueDate: boundedDateString("scheduled").nullish(),
  idempotencyKey,
}).superRefine((value, ctx) => {
  const named = [value.userId, value.vendorContactId].filter(
    (v) => v !== null && v !== undefined,
  ).length;
  if (named > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "a package is assigned to one worker: userId or vendorContactId, not both",
    });
  }
});
export type CreateWorkPackageBody = z.infer<typeof createWorkPackageBody>;

export interface CreateWorkPackageResult {
  workOrderId: string;
  visitWorkOrderId: string;
  issueCount: number;
}

/**
 * The Gantt lane under the Repair overview's kanban — 18 September 2026.
 *
 * ── The three filters are the KANBAN's three, spelled the same ───────
 * `assetClass`, `status` and `q` are exactly `kanbanBoardQuery`'s, because the
 * chart sits under that board and answers the same question one axis along. A
 * fourth filter here, or a different spelling of one of these, would let the two
 * halves of one screen disagree about which work they are describing — and the
 * disagreement would be invisible, since both would render perfectly.
 *
 * `assetClass` is OPTIONAL here where the per-cell `kanbanQuery` requires one:
 * the board is sixteen cells each naming its own class, and the chart is one
 * picture of the lot. Absent means every class, which is the default the
 * overview uses.
 *
 * ── No `limit` and no `offset`, deliberately ─────────────────────────
 * A lane chart that paged would be a chart of SOME of a mechanic's work, and a
 * manager reading "three bars" would have no way to know there were five. The
 * bound is a fixed cap in the reader instead, and when it bites the response
 * says so through `truncated` rather than quietly showing fewer bars.
 * Accepting an offset would invite a caller to think this pages.
 */
export const workOrderTimelineQuery = strictObject({
  assetClass: z.enum(assetClassFilters).optional(),
  status: z.enum(["open", "completed", "cancelled"]).default("open"),
  q: z.string().trim().max(200).optional(),
});
export type WorkOrderTimelineQuery = z.infer<typeof workOrderTimelineQuery>;

/* ── receipt reading (docs/plans/trbud-receipt-review-modes.md) ─────── */

/**
 * `GET /review?tab=`. The queue's three tabs, filtered by the server so a tab's
 * badge and its rows come from one snapshot. `offset` pages Posted, which only
 * grows; the other two tabs are worklists and are not paged.
 */
export const reviewQueueQuery = strictObject({
  search: z.string().trim().max(200).optional(),
  tab: z.enum(["unaudited", "logged", "posted"]).default("unaudited"),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});
export type ReviewQueueQuery = z.infer<typeof reviewQueueQuery>;

const readingModeValue = z.enum(["off", "manual", "auto"]).nullable();

/**
 * The version a receipt reading setting was read at.
 *
 * `set_receipt_reading_mode` and `set_receipt_parser_choice` take it as an
 * `integer`, so its type bounds it (TRBUD-329). With no upper bound, 3000000000
 * failed inside Postgres as 22003 and answered 500 instead of 400.
 */
const settingsVersion = z.number().int().min(1).max(INT4_MAX);

/**
 * `PUT /review/reading-mode`. `mode: null` goes back to following the
 * environment. `expectedVersion` is the `modeVersion` the page read; a stale one
 * answers 409.
 */
export const setReceiptReadingModeBody = strictObject({
  mode: readingModeValue,
  expectedVersion: settingsVersion,
});
export type SetReceiptReadingModeBody = z.infer<typeof setReceiptReadingModeBody>;

/**
 * `PUT /settings/receipt-reading`. Either key may be sent; each has its own
 * version, so a mode change and a parser change never collide. `parserId: null`
 * goes back to the environment's parser.
 */
export const setReceiptReadingSettingsBody = strictObject({
  readingMode: strictObject({ mode: readingModeValue, expectedVersion: settingsVersion }).optional(),
  parser: strictObject({
    parserId: z.string().uuid().nullable(),
    expectedVersion: settingsVersion,
  }).optional(),
});
export type SetReceiptReadingSettingsBody = z.infer<typeof setReceiptReadingSettingsBody>;

/** A threshold is a fraction: 0.8 is the 80% line. */
const parserThreshold = z.number().min(0).max(1);

/** `POST /settings/receipt-reading/versions`. A Foundry agent version is a whole number. */
export const registerReceiptParserVersionBody = strictObject({
  version: z
    .string()
    .trim()
    .regex(/^[0-9]{1,6}$/, "A Foundry agent version is a whole number of up to six digits."),
  threshold: parserThreshold,
});
export type RegisterReceiptParserVersionBody = z.infer<typeof registerReceiptParserVersionBody>;

/** `PATCH /settings/receipt-reading/versions/:parserId`. Send `active`, `threshold`, or both. */
export const updateReceiptParserVersionBody = strictObject({
  active: z.boolean().optional(),
  threshold: parserThreshold.optional(),
});
export type UpdateReceiptParserVersionBody = z.infer<typeof updateReceiptParserVersionBody>;

/** Warranty terms on an inventory part, rather than on an in-house work order. */
export const setPartWarrantyBody = strictObject({
  covered: z.boolean(),
  note: z.string().trim().max(WARRANTY_NOTE_MAX).nullable(),
  idempotencyKey,
});
export type SetPartWarrantyBody = z.infer<typeof setPartWarrantyBody>;

/** A user-entered link to the exact inventory product. Null clears it. */
export const setPartProductUrlBody = strictObject({
  productUrl: z.string().trim().max(2048).url().refine((value) => {
    try {
      const url = new URL(value);
      return (url.protocol === "https:" || url.protocol === "http:") && !url.username && !url.password;
    } catch { return false; }
  }, "Enter an http or https product link without login credentials.").nullable(),
  idempotencyKey,
});
export type SetPartProductUrlBody = z.infer<typeof setPartProductUrlBody>;
